import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ErroApi } from '../api';
import {
  ACONDICIONAMENTO, faixaProfundidade, paraNumero, plural, rotulo,
  rotuloDoAcondicionamento, TIPOS_AMOSTRA,
} from '../formato';
import {
  descartarPrevias, enviarFotos, FotosDaAmostra, FotosDoLote, minimoCumprido,
  MINIMO, ordenarParaEnvio, Progresso, type FotoPendente,
} from '../Fotos';
import { Area, Campo, Carregando, Escolha, ErroDaApi, type Opcao } from '../ui';
import {
  CadastroRapidoCliente, useBusca, type Cliente,
} from '../cadastros';
import { useSessao } from '../sessao';
import { verEquipes, type Equipe } from '../api/campo';
import { chaveDoNome } from '../nomeDaAmostra';
import { EscopoDaOs } from './FaixaDoEscopo';

/**
 * O assistente de recebimento — quatro passos, uma gravação.
 *
 * O desenho todo serve ao `CA-23`: receber um lote de 12 amostras em tempo
 * igual ou menor que hoje, pelo celular. Daí as escolhas:
 *
 * **Uma gravação só, no fim.** A API recebe o lote inteiro numa transação
 * (`recebimento.controller.ts`). Gravar passo a passo criaria recebimento
 * pela metade quando o sinal cai no meio do galpão — e recebimento pela
 * metade é um documento do SGQ inconsistente para alguém reconciliar à mão.
 *
 * **Uma amostra por tela** (decisão `D-19`, 20/08/2026). A lista com tudo
 * aberto ao mesmo tempo era rápida de ler e fácil de errar: no celular, um
 * toque fora do lugar digita a profundidade da amostra 7 no campo da 8, e o
 * erro só aparece quando o corpo de prova já está na prensa.
 *
 * **As fotos são pedidas na hora da amostra** (decisão `D-20`), não num
 * passo depois. Duas delas são obrigatórias — embalagem externa e etiqueta.
 * Um passo separado no fim significa fotografar de memória, com a caixa já
 * de volta na prateleira.
 *
 * **Rascunho automático.** O que foi digitado sobrevive a fechar o
 * navegador. As FOTOS não entram no rascunho — arquivo não cabe no
 * armazenamento local — e a tela diz isso em vez de fingir que salvou.
 */

/**
 * Uma falta do formulário — a frase, e o campo de que ela fala.
 *
 * `campo` é opcional porque nem toda falta tem um lugar único para onde
 * apontar: «a amostra 3 tem profundidade final menor que a inicial» é
 * uma linha de uma tabela, e mandar o foco para ela exigiria a tabela
 * inteira ter âncora por linha — que é outra onda. O que tem campo,
 * aponta; o que não tem, continua sendo texto.
 */
interface Falta { texto: string; campo?: string }

const CHAVE_RASCUNHO = 'testesolo.rascunho.recebimento';

const ORIGENS: Opcao[] = [
  'equipe_propria', 'cliente', 'empresa_sondagem', 'controle_obra',
].map((v) => ({ valor: v, texto: rotulo(v) }));

const DECISOES: Opcao[] = [
  'aceite', 'aceite_com_ressalva', 'rejeicao',
].map((v) => ({ valor: v, texto: rotulo(v) }));

const OPCOES_TIPO: Opcao[] = TIPOS_AMOSTRA.map((v) => ({ valor: v, texto: rotulo(v) }));

/*
 * 269 (etapa C da ideia do recebimento no celular): três passos. As fotos
 * do lote — todas opcionais — saíram de um passo próprio e moram no
 * «Fechar», fechadas, ao lado da decisão e da conferência.
 */
const PASSOS = ['Lote', 'Amostras', 'Fechar'] as const;

interface LinhaAmostra {
  chave: string;
  identificacaoRecebida: string;
  pontoInvestigacao: string;
  profundidadeInicial: string;
  profundidadeFinal: string;
  camada: string;
  quantidadeAproximada: string;
  tipoAmostra: string;
  tipoAcondicionamento: string;
  acondicionamentoDescricao: string;
  condicoesEmbalagem: string;
  osAmostraPrevistaId: string | null;
  observacoes: string;
  /**
   * A prevista que a pessoa disse «não é esta» (onda 251, `RN-220`).
   *
   * A sugestão pelo nome é sugestão: quem está com o saco na mão sabe
   * que «SP-04/3» do cliente não é a «SP-04/3» que a OS previu (outro
   * furo, outra campanha). Recusada uma vez, ela não volta a pedir —
   * nem depois de a pessoa mexer em outro campo da mesma amostra.
   */
  sugestaoRecusada: string | null;
}

/** Um local do cadastro de armazenamento (0062). */
interface LocalOpcao {
  id: string; nome: string; tipo_nome: string;
  armazenadas: number; situacao: string;
}

/**
 * O que ainda vale para o lote inteiro e é herdado por cada amostra.
 *
 * 269 (`RN-226`): o FURO não é mais do lote. Um lote traz sacos do SP-01 e
 * do SP-04, Shelbys do SP-02 — e a amostra que ficava em branco herdava,
 * calada, o furo de outra. O campo continua no tipo só para ler o rascunho
 * de uma versão anterior, que o passa para as amostras sem furo.
 */
interface Herdado {
  pontoInvestigacao: string;
  dataColeta: string;
  localizacaoFisica: string;
  /* O local do CADASTRO (0062). `localizacaoFisica` é o detalhe em texto
     livre — «3ª prateleira, fundo» —, e os dois convivem: cadastro é o
     que impede «camara umida», «Câmara Úmida» e «CU» de virarem três
     lugares (doc 48). */
  localId: string;
}

interface Rascunho {
  clienteId: string | null;
  clienteNome: string | null;
  osId: string | null;
  osNumero: string | null;
  origem: string;
  datahora: string;
  entregaNome: string;
  entregaEmpresa: string;
  entregaEquipeId: string | null;
  entregaDoc: string;
  herdado: Herdado;
  amostras: LinhaAmostra[];
  decisao: string;
  divergencias: string;
  ressalvas: string;
  /**
   * Como as amostras chegaram (onda 251, `RN-220`): marcando as previstas
   * da OS, ou uma a uma. Vazio enquanto a pessoa não escolheu — e só se
   * pergunta quando a OS tem prevista por chegar.
   */
  chegada: '' | 'previstas' | 'uma';
}

let seq = 0;
const novaLinha = (base?: Partial<LinhaAmostra>): LinhaAmostra => ({
  chave: `a${++seq}`,
  identificacaoRecebida: '',
  pontoInvestigacao: '',
  profundidadeInicial: '',
  profundidadeFinal: '',
  camada: '',
  quantidadeAproximada: '',
  tipoAmostra: '',
  tipoAcondicionamento: '',
  acondicionamentoDescricao: '',
  condicoesEmbalagem: '',
  osAmostraPrevistaId: null,
  observacoes: '',
  sugestaoRecusada: null,
  ...base,
});

/**
 * A amostra já tem alguma coisa digitada (ou fotografada)?
 *
 * É o que separa a linha em branco que o assistente cria sozinho da
 * linha em que alguém já trabalhou: a primeira pode sumir quando a
 * pessoa marca as previstas; a segunda nunca some sem um gesto dela.
 */
function temDado(a: LinhaAmostra, temFoto = false): boolean {
  return temFoto || !!(a.identificacaoRecebida.trim() || a.pontoInvestigacao.trim()
    || a.profundidadeInicial.trim() || a.profundidadeFinal.trim() || a.tipoAmostra
    || a.camada.trim() || a.condicoesEmbalagem.trim() || a.observacoes.trim());
}

/** Agora, no formato que `<input type="datetime-local">` exige. */
function agoraLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const rascunhoVazio = (): Rascunho => ({
  clienteId: null, clienteNome: null,
  osId: null, osNumero: null,
  origem: '',
  datahora: agoraLocal(),
  entregaNome: '', entregaEmpresa: '', entregaEquipeId: null, entregaDoc: '',
  herdado: {
    // Data da coleta nasce VAZIA (Testesolo, 20/08): quem recebe raramente
    // sabe quando o material foi coletado, e a data de hoje preenchida por
    // padrão viraria uma data de coleta errada em todo lote.
    pontoInvestigacao: '', dataColeta: '', localizacaoFisica: '', localId: '',
  },
  amostras: [novaLinha()],
  decisao: '',
  divergencias: '', ressalvas: '',
  chegada: '',
});

interface OrdemServico {
  id: string; numero: string; obra_nome: string | null;
  cliente: string | null; cliente_id: string | null;
}
interface AmostraPrevista {
  id: string; ordem: number; identificacao_prevista: string | null;
  origem_prevista: string | null; atendida: boolean;
  amostra_codigo: string | null; ensaios: string | null;
  /** 0188: o que a programação já sabia da amostra antes de ela chegar. */
  tipo_amostra?: 'deformada' | 'indeformada' | 'cp_moldado' | null;
  profundidade_inicial?: string | null;
  profundidade_final?: string | null;
  /** 0041: prevista fechada por revisão da OS não chega mais. */
  vigente?: boolean;
  /** 0162: o nome que o cliente deu, quando diferente do da OS. */
  identificacao_cliente?: string | null;
  /** 0162: os ensaios marcados, por código (a bateria inteira vira o nome dela). */
  ensaios_codigos?: Array<{ codigo: string; bateria?: boolean }> | null;
}

/**
 * As previstas que ainda podem chegar — vigentes e sem amostra.
 *
 * É a lista que o recebimento oferece (onda 251): a que já chegou noutro
 * lote não se marca de novo (o banco recusaria — `amostra.os_amostra_
 * prevista_id` é único), e a que a revisão da OS fechou não é mais
 * esperada.
 */
const porChegar = (previstas: AmostraPrevista[]) =>
  previstas.filter((p) => !p.atendida && p.vigente !== false);

/** O nome da prevista como a OS o mostra. */
const nomeDaPrevista = (p: AmostraPrevista) =>
  p.identificacao_prevista?.trim() || `Prevista ${p.ordem}`;

/**
 * Os ensaios marcados na prevista, pelo CÓDIGO — «UMID, GRAN», ou o nome
 * da bateria marcada por inteiro (0162). É o idioma da grade e do escopo;
 * `ensaios` (o nome por extenso) fica de reserva para a visão antiga.
 */
const ensaiosDaPrevista = (p: AmostraPrevista) =>
  (p.ensaios_codigos ?? []).map((e) => e.codigo).filter(Boolean).join(', ')
    || p.ensaios || '';

/**
 * A escolha feita na entrada do passo das amostras (onda 251).
 *
 * `marcadas`: as previstas que vieram nesta entrega. `manter`: as linhas
 * já trabalhadas que não são de prevista nenhuma (chave da linha).
 * `novas`: quantas amostras sem previsão a pessoa disse que chegaram.
 */
interface Entrada { marcadas: string[]; manter: string[]; novas: number }

/** O que falta para sair da entrada — a mesma frase no clique e depois dele. */
/**
 * 348: o nome do tipo de ponto como a Testesolo fala — o mesmo da
 * `fn_ponto_tipo_nome` (0274), para a frase da tela e a do banco baterem.
 */
function nomeDoTipoDePonto(tipo?: string | null): string {
  switch (tipo) {
    case 'cpt': return 'CPT';
    case 'cptu': return 'CPTu';
    case 'scptu': return 'SCPTu';
    case 'dmt': return 'DMT';
    case 'sdmt': return 'SDMT';
    case 'vane': return 'Vane Test';
    case 'permeabilidade': return 'ensaio de permeabilidade';
    case 'placa': return 'prova de carga em placa';
    case 'instrumento': return 'instrumento';
    default: return tipo ?? 'ponto';
  }
}

function faltasDaEntrada(chegada: Rascunho['chegada'], e: Entrada): Falta[] {
  if (!chegada) {
    return [{ campo: 'chegada',
      texto: 'Diga como as amostras chegaram — marcando as previstas da OS, ou uma a uma.' }];
  }
  if (chegada === 'previstas' && e.marcadas.length + e.manter.length + e.novas === 0) {
    return [{ campo: 'chegada',
      texto: 'Marque ao menos uma prevista que chegou — ou acrescente a amostra que chegou sem previsão.' }];
  }
  return [];
}

/**
 * Quem recebeu não se escolhe mais (0186).
 *
 * «Hoje o Recebido por é definido manualmente. O recebimento deve ser
 *  vinculado ao usuário que está preenchendo o relatório de recebimento»
 * — Humberto, 21/09/2026.
 *
 * O caminho até aqui: na onda 130 o campo era uma parede de quinze
 * botões de nome; passou a vir marcado com quem estava logado, com um
 * «trocar» ao lado. O «trocar» é que estava errado. O relatório de
 * recebimento é documento do SGQ, e documento do SGQ não tem campo de
 * autoria que a pessoa digita: quem assina é quem lançou. Agora a tela
 * só DIZ o nome, e o servidor o resolve pela ficha da conta (`RN-185`)
 * — a tela não tem como mandar outro, nem por engano.
 *
 * Se a conta não tiver ficha de funcionário, não há quem assine: a
 * recusa aparece aqui, antes de o técnico preencher trinta amostras.
 */
function RecebidoPor({ nome, temFicha }: {
  nome: string | null; temFicha: boolean;
}) {
  if (!temFicha) {
    return (
      <div className="campo" data-campo="recebido-por">
        <span className="rotulo">Recebido por</span>
        <div className="aviso atencao">
          A sua conta não tem ficha de funcionário, e o relatório de
          recebimento é assinado por quem o lança. Peça a um gestor do
          laboratório para vincular a sua conta à sua ficha.
        </div>
      </div>
    );
  }
  return (
    <div className="campo" data-campo="recebido-por">
      <span className="rotulo">Recebido por</span>
      <p className="recebido-por"><strong>{nome}</strong></p>
      <span className="dica">
        É o nome que assina o relatório, e vem de quem está usando o
        sistema: quem lança o recebimento é quem o recebeu.
      </span>
    </div>
  );
}


/** O corpo do `POST /recebimento`, a partir do rascunho. Lança se um
 *  campo estiver fora do formato — e quem chama mostra o erro. */
function montarCorpo(d: Rascunho) {
  return {
    clienteId: d.clienteId!,
    osId: d.osId ?? undefined,
    origem: d.origem,
    decisao: d.decisao,
    datahoraRecebimento: d.datahora ? new Date(d.datahora).toISOString() : undefined,
    entregaEquipeId: d.origem === 'equipe_propria' ? (d.entregaEquipeId ?? undefined) : undefined,
    responsavelEntregaNome: d.origem === 'equipe_propria' ? undefined : (d.entregaNome.trim() || undefined),
    responsavelEntregaEmpresa: d.origem === 'equipe_propria' ? undefined : (d.entregaEmpresa.trim() || undefined),
    responsavelEntregaDoc: d.entregaDoc.trim() || undefined,
    divergencias: d.divergencias.trim() || undefined,
    ressalvas: d.ressalvas.trim() || undefined,
    herdado: {
      /* 269 (`RN-226`): o furo é sempre da amostra — o lote não manda. */
      dataColeta: d.herdado.dataColeta || undefined,
      localizacaoFisica: d.herdado.localizacaoFisica.trim() || undefined,
      localId: d.herdado.localId || undefined,
    },
    amostras: d.amostras.map((a) => ({
      identificacaoRecebida: a.identificacaoRecebida.trim() || undefined,
      tipoAmostra: a.tipoAmostra || undefined,
      tipoAcondicionamento: a.tipoAcondicionamento || undefined,
      acondicionamentoDescricao: a.tipoAcondicionamento === 'outro'
        ? a.acondicionamentoDescricao.trim() || undefined : undefined,
      condicoesEmbalagem: a.condicoesEmbalagem.trim() || undefined,
      osAmostraPrevistaId: a.osAmostraPrevistaId ?? undefined,
      pontoInvestigacao: a.pontoInvestigacao.trim() || undefined,
      profundidadeInicial: paraNumero(a.profundidadeInicial),
      profundidadeFinal: paraNumero(a.profundidadeFinal),
      camada: a.camada.trim() || undefined,
      quantidadeAproximada: paraNumero(a.quantidadeAproximada),
      observacoes: a.observacoes.trim() || undefined,
    })),
  };
}

export function Assistente() {
  const navegar = useNavigate();
  /* Quem está lançando — é quem vai assinar o recebimento (0186). */
  const { eu } = useSessao();
  const [params] = useSearchParams();
  const [passo, setPasso] = useState(0);
  /** Qual amostra está na tela, dentro do passo 2. */
  const [atual, setAtual] = useState(0);
  const [d, setD] = useState<Rascunho>(() => {
    try {
      const salvo = localStorage.getItem(CHAVE_RASCUNHO);
      if (salvo) {
        const r = JSON.parse(salvo) as Partial<Rascunho>;
        if (Array.isArray(r.amostras) && r.amostras.length) {
          /*
           * O rascunho salvo por uma VERSÃO ANTERIOR da tela.
           *
           * O rascunho fica no navegador por semanas, e a tela ganhou
           * campos nesse tempo (`entregaDoc`, `herdado.localId`,
           * `condicoesEmbalagem`, `observacoes`…). Restaurado como veio,
           * um campo ausente era `undefined`, e `gravar()` morria em
           * `.trim()` ANTES de mandar qualquer coisa — sem erro na tela,
           * sem requisição, sem nada no servidor. Foi o que aconteceu na
           * homologação em 03/09/2026 («ainda não consigo registrar o
           * recebimento ao clicar no botão»): o botão não fazia nada.
           *
           * Restaurar SOBRE o vazio garante que todo campo exista, com o
           * valor salvo quando houver. As chaves de React não sobrevivem
           * ao JSON com garantia de unicidade; regerar aqui evita duas
           * linhas com a mesma chave.
           */
          const vazio = rascunhoVazio();
          /* 269 (`RN-226`): o furo do lote de um rascunho antigo vai para
             as amostras que estavam sem — às claras, na tela de cada uma. */
          const furoDoLote = (r.herdado?.pontoInvestigacao ?? '').trim();
          return {
            ...vazio,
            ...r,
            herdado: { ...vazio.herdado, ...(r.herdado ?? {}), pontoInvestigacao: '' },
            amostras: r.amostras.map((a) => novaLinha({
              ...a, chave: undefined,
              pontoInvestigacao: (a.pontoInvestigacao ?? '').trim() || furoDoLote,
            })),
          };
        }
      }
    } catch { /* rascunho ilegível: começa limpo */ }
    return rascunhoVazio();
  });
  const [fotos, setFotos] = useState<FotoPendente[]>([]);
  const [previstas, setPrevistas] = useState<AmostraPrevista[]>([]);
  /** 269: os pontos de investigação da OS (módulo de campo). */
  const [pontosDaOs, setPontosDaOs] = useState<string[]>([]);
  /**
   * 348 (`RN-270`): os pontos da OS que NÃO coletam amostra (CPTu, DMT,
   * Vane…), pelo código em minúsculas, com o nome do tipo para a frase.
   * Eles não viram botão, e o furo digitado igual a um deles é recusado
   * antes da viagem — o banco recusa também.
   */
  const [semColeta, setSemColeta] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!d.osId) { setPontosDaOs([]); setSemColeta(new Map()); return; }
    let vivo = true;
    api<Array<{ codigo: string; tipo?: string | null; situacao?: string;
      coleta_amostra?: boolean }>>(`/os/${d.osId}/pontos`)
      .then((l) => {
        if (!vivo) return;
        setPontosDaOs(l.filter((p) => p.coleta_amostra !== false
          && !['cancelado', 'suspenso'].includes(p.situacao ?? '')).map((p) => p.codigo));
        setSemColeta(new Map(l.filter((p) => p.coleta_amostra === false)
          .map((p) => [p.codigo.trim().toLocaleLowerCase('pt-BR'), nomeDoTipoDePonto(p.tipo)])));
      })
      .catch(() => { if (vivo) { setPontosDaOs([]); setSemColeta(new Map()); } });
    return () => { vivo = false; };
  }, [d.osId]);
  /**
   * 303 (RN-248): os nomes que JÁ existem nas amostras desta OS. Dentro da
   * OS a identificação do cliente não se repete — é por ela que a amostra
   * aparece no sistema inteiro. O banco recusa também (0249); aqui a
   * recusa vem antes da viagem, na própria amostra, com a sugestão.
   */
  const [nomesDaOs, setNomesDaOs] = useState<Array<{
    codigo: string; identificacao_recebida: string | null;
    status?: string; amostra_pai_id?: string | null;
  }>>([]);
  useEffect(() => {
    if (!d.osId) { setNomesDaOs([]); return; }
    let vivo = true;
    api<Array<{ codigo: string; identificacao_recebida: string | null;
      status?: string; amostra_pai_id?: string | null }>>(`/amostras?os=${d.osId}`)
      .then((l) => { if (vivo) setNomesDaOs(l); })
      .catch(() => { if (vivo) setNomesDaOs([]); });
    return () => { vivo = false; };
  }, [d.osId]);
  /**
   * 269 (`RN-226`): os furos que viram botão — os pontos da OS, os furos
   * das previstas e os já usados neste lote, sem repetir, em ordem natural.
   */
  const furosDoLote = useMemo(() => {
    const todos = new Set<string>();
    for (const f of pontosDaOs) if (f.trim()) todos.add(f.trim());
    for (const p of previstas) if (p.origem_prevista?.trim()) todos.add(p.origem_prevista.trim());
    /* As OUTRAS amostras do lote: a que está na tela não vira botão
       enquanto o furo dela é digitado (o campo se esvaziaria no meio). */
    d.amostras.forEach((a, i) => {
      if (i !== atual && a.pontoInvestigacao.trim()) todos.add(a.pontoInvestigacao.trim());
    });
    /* 348: vertical de CPTu (e todo ponto que não coleta) não é botão,
       venha de onde vier — nem da prevista antiga que a nomeava. */
    return [...todos]
      .filter((f) => !semColeta.has(f.toLocaleLowerCase('pt-BR')))
      .sort((x, y) => x.localeCompare(y, 'pt-BR', { numeric: true, sensitivity: 'base' }));
  }, [pontosDaOs, previstas, d.amostras, atual, semColeta]);

  /**
   * A OS que veio no endereço.
   *
   * «E terá um botão Receber amostras para cada OS em aberto»
   * (Testesolo, 02/09/2026). O botão da lista de espera abre o
   * assistente com `?os=<id>`, e chegar aqui já com a OS escolhida é o
   * que torna o botão útil: sem isso ele seria só um atalho para o
   * mesmo formulário em branco, e o laboratorista digitaria de novo o
   * número que acabou de ler na tela.
   *
   * Só na montagem, e só se o rascunho não tiver OS: um rascunho a meio
   * caminho de OUTRA OS não pode ser sequestrado por um endereço.
   */
  const osDoEndereco = params.get('os');
  /**
   * O rascunho a meio caminho de OUTRA OS.
   *
   * O link não sobrescreve o rascunho — de propósito, senão o técnico
   * perderia doze amostras digitadas sem pedir nada. Mas até aqui ele
   * fazia isso EM SILÊNCIO: abria-se o link da OS 2609111 e o formulário
   * estava preenchido com outra, sem uma palavra. Quem não percebe
   * preenche em cima e só descobre no fim, quando o lote nasce amarrado
   * à OS errada.
   */
  const osDivergente = !!osDoEndereco && !!d.osId && d.osId !== osDoEndereco;

  /**
   * A OS do endereço, guardada.
   *
   * Ela é buscada UMA vez e fica aqui, porque dois gestos precisam dela:
   * o preenchimento automático da abertura e o botão «começar do zero
   * com a OS do link». A primeira versão do botão só limpava o rascunho
   * — e o formulário ficava em branco, sem a OS, porque o efeito de
   * preenchimento não roda de novo. Limpar sem trazer a OS troca um beco
   * por outro.
   */
  const [osDoLink, setOsDoLink] = useState<{
    id: string; numero: string; obra_nome: string | null;
    cliente_id: string; cliente: string | null;
  } | null>(null);

  const aplicarOsDoLink = useCallback((os: NonNullable<typeof osDoLink>) => ({
    osId: os.id,
    osNumero: `${os.numero} — ${os.obra_nome ?? ''}`,
    clienteId: os.cliente_id,
    clienteNome: os.cliente,
  }), []);

  useEffect(() => {
    if (!osDoEndereco) return;
    let vivo = true;
    api<NonNullable<typeof osDoLink>>(`/os/${osDoEndereco}`)
      .then((os) => {
        if (!vivo) return;
        setOsDoLink(os);
        /* Só preenche se não houver rascunho em andamento (`osDivergente`). */
        setD((r) => (r.osId ? r : { ...r, ...aplicarOsDoLink(os) }));
      })
      .catch(() => { /* OS sumiu ou sem permissão: o formulário abre em branco */ });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osDoEndereco]);
  const [erro, setErro] = useState<unknown>(null);
  /*
   * A recusa carrega o CAMPO, e não só a frase.
   *
   * «Ainda não consigo registrar o recebimento de amostra» — Testesolo,
   * 02/09/2026, pela terceira vez. Dirigi a tela e vi o que acontece: o
   * botão «Registrar recebimento» está HABILITADO, o clique não envia
   * nada, e a resposta é o texto «Registre a decisão do recebimento»
   * numa tarja — enquanto os três botões da decisão (Aceite / Aceite com
   * ressalva / Rejeição) continuam com a mesma cara de sempre, lá em
   * cima, indistinguíveis de qualquer outro botão da tela.
   *
   * Faltavam duas coisas, e as duas são de mira:
   *
   *   · a frase e o campo não se reconhecem — a tarja diz «a decisão do
   *     recebimento», o rótulo pergunta «O laboratório aceita este
   *     material?», e ninguém liga uma coisa à outra;
   *   · nada leva do texto ao campo. Ler «falta a decisão» e ter de
   *     procurá-la numa página de quatro painéis é procurar, não
   *     corrigir.
   *
   * Agora cada falta sabe o campo de que fala, o texto vira um LINK que
   * rola até ele e o marca, e o campo em falta se anuncia sozinho.
   */
  const [reclamacoes, setReclamacoes] = useState<Falta[]>([]);
  /** O campo que a última recusa apontou — ele se pinta até ser resolvido. */
  const [emFalta, setEmFalta] = useState<string | null>(null);
  const [gravando, setGravando] = useState<null | { etapa: string; fracao: number }>(null);

  /**
   * A recusa tem de chegar aos olhos de quem apertou o botão.
   *
   * «Ao clicar em registrar recebimento, a tela não vai adiante, fica
   *  estática» — Testesolo, 02/09/2026. E estava mesmo: o aviso do que
   * falta é pintado no TOPO do formulário, e o botão «Registrar
   * recebimento» fica no RODAPÉ de uma página de quatro painéis. Quem
   * aperta está a mil pixels do lugar onde a resposta aparece — a tela
   * respondeu, e a resposta nasceu fora da vista.
   *
   * Rolar até o aviso e pôr o foco nele conserta as duas metades: o
   * olho vê, e o leitor de tela lê. É a mesma recusa de antes; o que
   * muda é ela deixar de ser silenciosa.
   */
  const avisoRef = useRef<HTMLDivElement>(null);
  const erroRef  = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /* O erro do servidor tem precedência: ele é a resposta a um envio
       que chegou a sair, e a lista de faltas é o que sobrou de antes. */
    const el = (erro ? erroRef.current : null) ?? avisoRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus();
  }, [reclamacoes, erro]);

  const mudar = <K extends keyof Rascunho>(campo: K, valor: Rascunho[K]) =>
    setD((r) => ({ ...r, [campo]: valor }));

  // Rascunho a cada mudança. Sem isto, fechar o navegador no passo 3 perde
  // doze amostras digitadas.
  useEffect(() => {
    try { localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(d)); } catch { /* modo privado */ }
  }, [d]);

  // Aviso do navegador ao fechar com trabalho por gravar.
  useEffect(() => {
    const temTrabalho = () =>
      !!d.origem || !!d.clienteId || fotos.length > 0 ||
      d.amostras.some((a) => a.identificacaoRecebida || a.profundidadeInicial);
    const aoSair = (e: BeforeUnloadEvent) => { if (temTrabalho()) e.preventDefault(); };
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [d, fotos]);

  const fotosRef = useRef(fotos);
  fotosRef.current = fotos;
  useEffect(() => () => descartarPrevias(fotosRef.current), []);

  /**
   * A programação prévia da OS escolhida (migração `0013`).
   *
   * Se a OS tem amostras programadas, o técnico amarra o que chegou ao que
   * estava previsto — e os ensaios vêm junto. Sem OS, não há o que amarrar.
   */
  useEffect(() => {
    if (!d.osId) { setPrevistas([]); return; }
    let vivo = true;
    api<AmostraPrevista[]>(`/os/${d.osId}/amostras-previstas`)
      .then((r) => {
        if (!vivo) return;
        setPrevistas(r);
        /*
         * Trocou a OS no passo do lote? A ligação com a prevista da OS
         * anterior não serve mais — o banco recusaria («a amostra prevista
         * é de outra OS») só no fim, com tudo digitado. Solta aqui, em
         * silêncio: o nome e o resto que a pessoa escreveu ficam.
         */
        const ids = new Set(r.map((p) => p.id));
        setD((x) => (x.amostras.some((a) => a.osAmostraPrevistaId && !ids.has(a.osAmostraPrevistaId))
          ? { ...x, amostras: x.amostras.map((a) => (a.osAmostraPrevistaId && !ids.has(a.osAmostraPrevistaId)
            ? { ...a, osAmostraPrevistaId: null } : a)) }
          : x));
      })
      .catch(() => { if (vivo) setPrevistas([]); });
    return () => { vivo = false; };
  }, [d.osId]);

  /** As previstas da OS que ainda podem chegar (onda 251). */
  const disponiveis = useMemo(() => porChegar(previstas), [previstas]);

  /**
   * A entrada do passo das amostras: «Como as amostras chegaram?».
   *
   * «Caso já tenha sido previsto a nomenclatura das amostras, essa
   *  nomenclatura já irá constar no recebimento» — Humberto, 02/10/2026
   * (D5 da ideia do fluxo da OS). Quando a OS tem prevista por chegar, o
   * passo das amostras não começa por uma ficha em branco: começa pela
   * lista das previstas, com o nome, o furo, a profundidade e os ensaios
   * que a OS já marcou. Quem recebe marca as que vieram, e cada uma vira
   * uma amostra LIGADA à prevista — que é o que faz os ensaios marcados
   * virarem ensaio de verdade na chegada (gatilho da 0161). Antes, a
   * ligação era escolhida amostra por amostra, num campo que quase
   * ninguém via, e o esquecimento deixava o mesmo saco em duas linhas.
   *
   * `null` é «não está na entrada». Não entra no rascunho: é posição na
   * tela, como `atual`.
   */
  const [entrada, setEntrada] = useState<Entrada | null>(null);

  const abrirEntrada = () => {
    const ids = new Set(disponiveis.map((p) => p.id));
    setEntrada({
      marcadas: d.amostras.map((a) => a.osAmostraPrevistaId)
        .filter((id): id is string => !!id && ids.has(id)),
      manter: d.amostras
        .filter((a, i) => !(a.osAmostraPrevistaId && ids.has(a.osAmostraPrevistaId))
          && temDado(a, fotos.some((f) => f.amostraIndice === i)))
        .map((a) => a.chave),
      novas: 0,
    });
    setReclamacoes([]);
    setPasso(1);
    setAtual(0);
    window.scrollTo(0, 0);
  };

  const semMinimo = useMemo(
    () => d.amostras.filter((_, i) => !minimoCumprido(fotos, i)).length,
    [d.amostras, fotos],
  );

  /** Confere UMA amostra. Devolve o que falta, já com o número dela. */
  const conferirAmostra = useCallback((a: LinhaAmostra, i: number): string[] => {
    const p: string[] = [];
    const n = `Amostra ${i + 1}`;
    const ini = paraNumero(a.profundidadeInicial);
    const fim = paraNumero(a.profundidadeFinal);
    if (a.profundidadeInicial && ini === undefined) {
      p.push(`${n}: profundidade inicial não é um número.`);
    }
    if (a.profundidadeFinal && fim === undefined) {
      p.push(`${n}: profundidade final não é um número.`);
    }
    if (ini !== undefined && fim !== undefined && fim < ini) {
      p.push(`${n}: a profundidade final é menor que a inicial.`);
    }
    /*
     * Tipo e acondicionamento são obrigatórios (04/09/2026): «não permita
     * uma amostra ser recebida sem determinar se ela é indeformada ou
     * deformada e sem descrever o acondicionamento dela». O servidor
     * recusa também; aqui a recusa vem antes da viagem. Furo/ponto e
     * profundidade continuam opcionais.
     */
    if (!a.tipoAmostra) {
      p.push(`${n}: diga se a amostra é deformada, indeformada ou CP moldado pelo cliente.`);
    } else if (!a.tipoAcondicionamento) {
      p.push(`${n}: diga como a amostra veio acondicionada.`);
    }
    if (a.tipoAcondicionamento === 'outro' && !a.acondicionamentoDescricao.trim()) {
      p.push(`${n}: diga o que é o acondicionamento "outro" — tubo de PVC, cilindro de CBR, lascas, balde.`);
    }
    /*
     * 303 (RN-248): o nome do cliente não se repete dentro da OS — nem
     * contra as amostras que já estão nela, nem dentro desta entrega.
     */
    const nome = a.identificacaoRecebida.trim();
    const chave = nome.toLocaleLowerCase('pt-BR');
    if (d.osId && chave) {
      const ja = nomesDaOs.find((x) => !x.amostra_pai_id && x.status !== 'rejeitada'
        && (x.identificacao_recebida ?? '').trim().toLocaleLowerCase('pt-BR') === chave);
      if (ja) {
        p.push(`${n}: a identificação «${nome}» já existe nesta OS (${ja.codigo}). `
          + `Renomeie a amostra nova — por exemplo «${nome}-B» — ou confira se é a mesma.`);
      }
      const antes = d.amostras.findIndex((x, j) => j !== i
        && x.identificacaoRecebida.trim().toLocaleLowerCase('pt-BR') === chave);
      if (antes >= 0 && antes < i) {
        p.push(`${n}: a identificação «${nome}» se repete nesta entrega (amostra ${antes + 1}). `
          + 'Dentro da OS cada amostra tem um nome só.');
      }
    }
    /*
     * 348 (`RN-270`): «no CPTu não tem coleta de amostra» (Humberto,
     * 22/10/2026). O furo digitado igual a uma vertical de CPTu (ou a
     * outro ponto da OS que não coleta) não passa; nome que não existe na
     * OS continua livre.
     */
    const furo = a.pontoInvestigacao.trim();
    const tipo = furo ? semColeta.get(furo.toLocaleLowerCase('pt-BR')) : undefined;
    if (tipo) {
      p.push(`${n}: «${furo}» é ${tipo} desta OS, e ${tipo} não coleta amostra. `
        + 'Escolha o furo de onde a amostra veio (SPT, trado, rotativa, mista ou poço).');
    }
    return p;
  }, [d.osId, d.amostras, nomesDaOs, semColeta]);

  const conferir = useCallback((ate: number): Falta[] => {
    const p: (Falta | string)[] = [];
    if (ate >= 0) {
      /*
       * Dizer "informe o cliente" mandava o técnico olhar para um campo
       * que ele JÁ tinha preenchido: o campo é uma busca, e digitar o
       * nome não escolhe ninguém. A frase precisa nomear o gesto que
       * falta, não o campo.
       */
      if (!d.clienteId) {
        p.push({ campo: 'cliente',
          texto: 'Escolha o cliente na lista abaixo do campo — digitar o '
            + 'nome não basta. Se ele não aparecer, use “Cadastrar”.' });
      }
      if (!d.origem) {
        p.push({ texto: 'Escolha a origem do material.', campo: 'origem' });
      }
      /*
       * Quem entregou é obrigatório (04/09/2026), e a forma segue a
       * origem: equipe própria escolhe a equipe; os demais escrevem o
       * nome completo da pessoa e a empresa. O servidor cobra o mesmo.
       */
      if (d.origem === 'equipe_propria') {
        if (!d.entregaEquipeId) {
          p.push({ texto: 'Escolha qual equipe trouxe o material.',
            campo: 'entregue-por' });
        }
      } else if (d.origem) {
        if (!d.entregaNome.trim()) {
          p.push({ texto: 'Escreva o nome completo de quem entregou.',
            campo: 'entregue-por' });
        }
        if (!d.entregaEmpresa.trim()) {
          p.push({ texto: 'Escreva o nome da empresa que entregou.',
            campo: 'entregue-por' });
        }
      }
      /*
       * Conta sem ficha de funcionário não recebe (0186).
       *
       * Antes a falta era «informe quem recebeu» — uma escolha que o
       * técnico tinha de fazer. Agora o recebedor é ele mesmo, e a única
       * falta possível é a conta não ter ficha atrás dela: aí ninguém
       * pode assinar, e o servidor vai recusar. Dizer isso aqui é dizer
       * antes das trinta amostras.
       */
      if (!eu?.funcionarioId) {
        p.push({ texto: 'A sua conta não tem ficha de funcionário, e o '
          + 'relatório de recebimento é assinado por quem o lança. Peça a '
          + 'um gestor do laboratório para vincular a sua conta à sua ficha.',
          campo: 'recebido-por' });
      }
    }
    if (ate >= 1) {
      if (!d.amostras.length) p.push('Registre ao menos uma amostra.');
      d.amostras.forEach((a, i) => p.push(...conferirAmostra(a, i)));
    }
    if (ate >= 2) {
      if (!d.decisao) {
        /*
         * A frase usa as MESMAS palavras do rótulo do campo.
         *
         * Dizer «registre a decisão» e o campo perguntar «o laboratório
         * aceita este material?» são duas frases sobre a mesma coisa, e
         * quem lê uma não acha a outra. Agora a recusa cita o rótulo.
         */
        p.push({ campo: 'decisao',
          texto: 'Responda «O laboratório aceita este material?» — '
            + 'Aceite, Aceite com ressalva ou Rejeição.' });
      }
      if (d.decisao === 'aceite_com_ressalva' && !d.ressalvas.trim()) {
        p.push({ campo: 'ressalvas',
          texto: 'Aceite com ressalva exige escrever qual é a ressalva.' });
      }
      if (d.decisao === 'rejeicao' && !d.divergencias.trim() && !d.ressalvas.trim()) {
        p.push({ campo: 'ressalvas', texto: 'Rejeição exige dizer o motivo.' });
      }
      /*
       * Aceitou, diga onde pôs (§6, doc 48).
       *
       * A trava está no servidor também — a tela nunca é a única guarda.
       * Mas cobrar aqui evita que o técnico preencha o recebimento
       * inteiro, aperte «registrar» e receba a recusa no fim, que é a
       * hora em que ele mais quer só terminar.
       *
       * Na rejeição não se cobra: o material rejeitado também ocupa
       * espaço, mas exigir cadastro de local para recusar uma entrega
       * criaria atrito exatamente quando o laboratório quer registrar
       * rápido e devolver.
       */
      if (d.decisao && d.decisao !== 'rejeicao' && !d.herdado.localId) {
        /*
         * A frase cita o rótulo do campo, palavra por palavra.
         *
         * Esta foi a falta que travou a Testesolo três vezes. O campo só
         * NASCE depois de a decisão ser escolhida — some antes, aparece
         * depois, abaixo da dobra —, e a recusa o chamava de «diga onde
         * o material foi posto» enquanto o rótulo pergunta «Onde este
         * material foi posto?». Duas frases sobre a mesma coisa, e nada
         * levando de uma à outra.
         */
        p.push({ campo: 'local',
          texto: 'Responda «Onde este material foi posto?» — a prateleira, '
            + 'a câmara úmida ou a bancada de recebimento. É a única hora '
            + 'em que alguém está com ele na mão.' });
      }
    }
    /* Texto solto vira `Falta` sem campo: uma forma só sai daqui. */
    return p.map((f) => (typeof f === 'string' ? { texto: f } : f));
  }, [d, conferirAmostra, eu]);

  /**
   * Levar o olho — e o foco — até o campo que falta.
   *
   * É a peça que faltava. A tarja dizia o que falta e o campo ficava a
   * mil pixels dela, com a mesma cara de sempre; ler a frase e ter de
   * procurar o campo numa página de quatro painéis é procurar, não
   * corrigir. Agora o texto da falta é um link: ele rola até o campo,
   * põe o foco no primeiro controle dele e o MARCA — e a marca só sai
   * quando o campo for respondido.
   */
  const irAoCampo = (campo: string) => {
    setEmFalta(campo);
    const el = document.querySelector<HTMLElement>(`[data-campo="${campo}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const foco = el.querySelector<HTMLElement>(
      'button:not([disabled]), input, textarea, select');
    (foco ?? el).focus?.();
  };

  /**
   * Depois da primeira recusa, a lista passa a se corrigir sozinha.
   *
   * Foi o segundo defeito que o teste na interface mostrou. Eu tinha
   * feito o link levar ao campo e o campo se marcar — e o técnico
   * respondia a decisão, a marca CONTINUAVA lá, e o botão continuava
   * recusando: a lista de faltas só era recalculada no clique, então
   * ela mentia sobre o estado atual do formulário.
   *
   * Antes da primeira recusa não se valida nada — cobrar campo que
   * ninguém tentou preencher ainda é pintar de vermelho um formulário
   * em branco. Depois dela, a lista é do estado, não do último clique.
   */
  useEffect(() => {
    if (!reclamacoes.length) return;
    const agora = passo === 1 && entrada
      ? faltasDaEntrada(d.chegada, entrada)
      : passo === 1
        ? (d.amostras[atual]
          ? conferirAmostra(d.amostras[atual], atual).map((t) => ({ texto: t }))
          : [])
        : conferir(passo);
    /* Só reescreve quando MUDA, senão o efeito se realimenta. */
    const mesmo = agora.length === reclamacoes.length
      && agora.every((r, i) => r.texto === reclamacoes[i].texto);
    if (!mesmo) setReclamacoes(agora);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, passo, atual, entrada]);

  /* Respondido o campo, a marca sai sozinha — ninguém tem de «fechar»
     um aviso que já não é verdade. */
  useEffect(() => {
    if (!emFalta) return;
    if (!reclamacoes.some((r) => r.campo === emFalta)) setEmFalta(null);
  }, [reclamacoes, emFalta]);

  const irPara = (p: number, amostra = 0) => {
    setReclamacoes([]);
    setEntrada(null);
    setPasso(p);
    setAtual(amostra);
    window.scrollTo(0, 0);
  };

  /**
   * "Próximo" dentro do passo das amostras anda de amostra em amostra; só
   * no fim da lista é que sai para o passo seguinte. É o que faz o passo 2
   * ser uma sequência, e não uma lista.
   */
  /**
   * Sair da entrada para as amostras, uma por tela (onda 251).
   *
   * «Marcar as previstas»: cada prevista marcada vira uma linha ligada a
   * ela — a linha que já existia para a mesma prevista fica como está
   * (com o que a pessoa digitou e as fotos); a nova nasce com o que a
   * previsão sabe (`daPrevisao`). Depois vêm as linhas já trabalhadas sem
   * prevista e as amostras novas sem previsão. A linha em branco que o
   * assistente criou sozinho some.
   *
   * As fotos guardam a POSIÇÃO da amostra, não a chave (ver
   * `removerAmostra`). Reordenar as linhas sem remapear as fotos trocaria
   * a evidência de uma amostra pela de outra — o remapeamento vai junto.
   */
  const confirmarEntrada = (e: Entrada) => {
    const faltas = faltasDaEntrada(d.chegada, e);
    setReclamacoes(faltas);
    if (faltas.length) return;
    if (d.chegada === 'previstas') {
      const ids = new Set(disponiveis.map((p) => p.id));
      const porPrevista = new Map(d.amostras
        .filter((a) => a.osAmostraPrevistaId && ids.has(a.osAmostraPrevistaId))
        .map((a) => [a.osAmostraPrevistaId!, a]));
      const linhas: LinhaAmostra[] = [];
      for (const p of disponiveis) {
        if (!e.marcadas.includes(p.id)) continue;
        const ja = porPrevista.get(p.id);
        linhas.push(ja ?? novaLinha({
          osAmostraPrevistaId: p.id, ...daPrevisao(p.id, previstas, novaLinha()),
        }));
      }
      for (const chave of e.manter) {
        const a = d.amostras.find((x) => x.chave === chave);
        /* Ligada a prevista que já não está por chegar (chegou noutro
           lote enquanto o rascunho esperava): a ligação cai, a linha fica. */
        if (a) linhas.push(a.osAmostraPrevistaId && !ids.has(a.osAmostraPrevistaId)
          ? { ...a, osAmostraPrevistaId: null } : a);
      }
      for (let i = 0; i < e.novas; i++) linhas.push(novaLinha());

      const destino = new Map(linhas.map((a, i) => [a.chave, i]));
      const deOnde = d.amostras.map((a) => destino.get(a.chave));
      setFotos(fotos.flatMap((f) => {
        if (f.amostraIndice === null) return [f];
        const novo = deOnde[f.amostraIndice];
        if (novo === undefined) { URL.revokeObjectURL(f.previa); return []; }
        return [{ ...f, amostraIndice: novo }];
      }));
      setD((r) => ({ ...r, amostras: linhas }));
    } else if (!d.amostras.length) {
      setD((r) => ({ ...r, amostras: [novaLinha()] }));
    }
    setEntrada(null);
    setReclamacoes([]);
    setAtual(0);
    window.scrollTo(0, 0);
  };

  const avancar = () => {
    if (passo === 1 && entrada) { confirmarEntrada(entrada); return; }
    if (passo === 1) {
      const a = d.amostras[atual];
      const p: Falta[] = (a ? conferirAmostra(a, atual) : [])
        .map((t) => ({ texto: t }));
      setReclamacoes(p);
      if (p.length) return;
      if (atual < d.amostras.length - 1) {
        setAtual(atual + 1);
        window.scrollTo(0, 0);
        return;
      }
      irPara(2);
      return;
    }
    const p = conferir(passo);
    setReclamacoes(p);
    if (p.length) return;
    /* Do lote para as amostras: com prevista por chegar, a entrada vem
       antes da primeira amostra (onda 251). */
    if (passo === 0 && disponiveis.length) { abrirEntrada(); return; }
    irPara(Math.min(passo + 1, PASSOS.length - 1), 0);
  };

  const voltar = () => {
    if (passo === 1 && entrada) { irPara(0); return; }
    if (passo === 1 && atual === 0 && disponiveis.length) { abrirEntrada(); return; }
    if (passo === 1 && atual > 0) {
      setReclamacoes([]);
      setAtual(atual - 1);
      window.scrollTo(0, 0);
      return;
    }
    if (passo === 2) { irPara(1, Math.max(d.amostras.length - 1, 0)); return; }
    irPara(Math.max(passo - 1, 0), 0);
  };

  const acrescentar = (base?: LinhaAmostra) => {
    const nova = novaLinha(base ? {
      // Repetir copia o que costuma se repetir e ZERA a profundidade —
      // que é justamente o campo que muda de uma amostra para a seguinte.
      // Copiar a profundidade seria convidar o erro mais caro deste
      // formulário: duas amostras com a mesma cota.
      pontoInvestigacao: base.pontoInvestigacao,
      quantidadeAproximada: base.quantidadeAproximada,
      tipoAmostra: base.tipoAmostra,
      tipoAcondicionamento: base.tipoAcondicionamento,
      acondicionamentoDescricao: base.acondicionamentoDescricao,
      condicoesEmbalagem: base.condicoesEmbalagem,
    } : undefined);
    setD((r) => ({ ...r, amostras: [...r.amostras, nova] }));
    setAtual(d.amostras.length);
    setReclamacoes([]);
    window.scrollTo(0, 0);
  };

  /**
   * Remover a amostra remove as fotos dela e REINDEXA as das seguintes.
   *
   * As fotos guardam a posição da amostra, não a chave. Sem o remapeamento,
   * apagar a amostra 3 faria a foto da 4 aparecer como sendo da 3 — e a
   * evidência do lote inteiro sairia trocada no documento.
   */
  const removerAmostra = (indice: number) => {
    setFotos((atuais) => {
      const sobrando: FotoPendente[] = [];
      for (const f of atuais) {
        if (f.amostraIndice === indice) { URL.revokeObjectURL(f.previa); continue; }
        sobrando.push(f.amostraIndice !== null && f.amostraIndice > indice
          ? { ...f, amostraIndice: f.amostraIndice - 1 }
          : f);
      }
      return sobrando;
    });
    setD((r) => ({ ...r, amostras: r.amostras.filter((_, i) => i !== indice) }));
    setAtual((n) => Math.max(0, Math.min(n, d.amostras.length - 2)));
  };

  /**
   * NADA que aconteça no clique fica em silêncio.
   *
   * «Não consigo registrar recebimento ao clicar no botão» (03/09/2026)
   * — com a tela inteira preenchida e nenhum aviso, e no servidor nenhum
   * POST. Uma exceção em qualquer trecho síncrono daqui (a conferência,
   * a montagem do corpo, um campo que a tela não previu) sumia no
   * console e o botão parecia não fazer nada. Agora toda falha vira a
   * caixa vermelha do topo, com a frase da exceção — e o console guarda
   * a pilha para quem for procurar.
   */
  async function gravar() {
    try {
      await gravarMesmo();
    } catch (e) {
      console.error('[recebimento] falha ao registrar', e);
      setErro(e instanceof Error ? e : new Error(String(e)));
      setGravando(null);
      window.scrollTo(0, 0);
    }
  }

  async function gravarMesmo() {
    const p = conferir(2);
    setReclamacoes(p);
    if (p.length) return;
    setErro(null);

    /*
     * Montar o corpo pode falhar — e quando falha, a pessoa precisa
     * ver. Um rascunho de outra versão, uma data inválida no campo, um
     * campo que a tela não previu: fora do `try`, a exceção sumia no
     * console e o botão parecia não fazer nada (03/09/2026).
     */
    let corpo;
    try {
      corpo = montarCorpo(d);
    } catch (e) {
      // Falha ao MONTAR o envio — nada saiu para o servidor.
      setErro(e instanceof Error ? e : new Error(String(e)));
      window.scrollTo(0, 0);
      return;
    }

    try {
      setGravando({ etapa: 'Registrando o lote…', fracao: 0 });
      const lote = await api<{
        id: string; codigo: string;
        amostras: Array<{ id: string; codigo: string }>;
        idsNaOrdemDoEnvio: string[];
      }>(
        '/recebimento', { corpo });

      /*
       * Onda 325 — as fotos na amostra certa.
       *
       * As fotos guardam a POSIÇÃO da amostra no formulário. `lote.amostras`
       * vem na ordem do LOTE (0185: a identificação do cliente, em ordem
       * natural), não na ordem em que foram digitadas; casar uma com a
       * outra pela posição pôs a foto de uma amostra na vizinha sempre que
       * a nomenclatura não vinha em ordem alfabética (15/10/2026). O
       * servidor devolve os ids alinhados ao corpo — é por eles que se casa.
       */
      const ids = Array.isArray(lote.idsNaOrdemDoEnvio) ? lote.idsNaOrdemDoEnvio : [];
      const codigoPorId = new Map(lote.amostras.map((a) => [a.id, a.codigo]));

      // O lote está gravado. Daqui para a frente uma falha não perde o
      // recebimento — perde no máximo as fotos, que a ficha permite anexar
      // depois. Por isso o rascunho já pode sair.
      try { localStorage.removeItem(CHAVE_RASCUNHO); } catch { /* modo privado */ }

      if (fotos.length && ids.length !== d.amostras.length) {
        // Sem o casamento confirmado, foto nenhuma sobe: melhor faltar do
        // que cair na amostra errada.
        descartarPrevias(fotos);
        navegar(`/recebimento/${lote.id}`, {
          state: {
            avisoFotos: `O recebimento ${lote.codigo} foi registrado, mas o servidor não `
              + 'confirmou as amostras na ordem em que foram enviadas — as fotos não '
              + 'foram anexadas para não cair na amostra errada. Anexe-as aqui.',
          },
        });
        return;
      }

      if (fotos.length) {
        const texto = `Enviando ${plural(fotos.length, 'foto', 'fotos')}…`;
        setGravando({ etapa: texto, fracao: 0 });
        try {
          await enviarFotos(lote.id, ordenarParaEnvio(fotos), ids, (f) =>
            setGravando({ etapa: texto, fracao: f }));
        } catch (e) {
          // Recebimento gravado, fotos não. Dizer isso é obrigatório: o
          // técnico precisa saber que precisa voltar e anexar.
          navegar(`/recebimento/${lote.id}`, {
            state: {
              avisoFotos: `O recebimento ${lote.codigo} foi registrado, mas as fotos não subiram `
                + `(${e instanceof Error ? e.message : 'falha no envio'}). Anexe-as aqui.`,
            },
          });
          return;
        }
      }

      /**
       * Concluir é o que o banco confere (migração `0012`): sem foto de
       * embalagem e de etiqueta em cada amostra, ele recusa — e a recusa
       * vem com os códigos das amostras que faltam. Falhar aqui NÃO é
       * perder nada: o recebimento fica como rascunho e a ficha diz o que
       * falta.
       */
      /**
       * Quando a tela JÁ SABE que falta foto, não pede a conclusão.
       *
       * Pedir para o servidor recusar é gastar uma viagem de rede que vai
       * falhar — no galpão, com sinal ruim, essa viagem pode custar meio
       * minuto de espera para chegar a uma recusa que a tela podia ter
       * escrito sozinha. Os códigos das amostras vêm na resposta da
       * gravação, então a mensagem sai igual à do banco.
       */
      const semFoto = d.amostras
        .map((_, i) => (minimoCumprido(fotos, i) ? null : codigoPorId.get(ids[i])))
        .filter(Boolean) as string[];

      if (semFoto.length) {
        descartarPrevias(fotos);
        navegar(`/recebimento/${lote.id}`, {
          state: {
            avisoFotos: `O recebimento ${lote.codigo} foi gravado, mas ficou como rascunho: `
              + `faltam as fotos de embalagem e etiqueta em ${semFoto.join(', ')}.`,
          },
        });
        return;
      }

      setGravando({ etapa: 'Concluindo o recebimento…', fracao: 1 });
      try {
        await api(`/recebimento/${lote.id}/concluir`, { metodo: 'POST' });
        descartarPrevias(fotos);
        navegar(`/recebimento/${lote.id}`, { state: { recemCriado: true } });
      } catch (e) {
        descartarPrevias(fotos);
        navegar(`/recebimento/${lote.id}`, {
          state: {
            avisoFotos: `O recebimento ${lote.codigo} foi gravado, mas ficou como rascunho: `
              + `${e instanceof Error ? e.message : 'falta evidência'}`,
          },
        });
      }
    } catch (e) {
      setErro(e);
      // Erro de validação vindo do servidor manda o técnico de volta ao
      // passo do campo culpado, em vez de deixá-lo procurando.
      if (e instanceof ErroApi && e.campos.length) {
        const campo = e.campos[0].campo;
        const m = /^amostras\.(\d+)/.exec(campo);
        if (m) irPara(1, Number(m[1]));
        else if (campo.startsWith('amostras')) irPara(1, 0);
        else if (['decisao', 'ressalvas', 'divergencias'].some((c) => campo.includes(c))) irPara(2);
        else irPara(0);
      }
      window.scrollTo(0, 0);
    } finally {
      setGravando(null);
    }
  }

  if (gravando) {
    return (
      <div className="cartao" role="status">
        <h2>{gravando.etapa}</h2>
        <p className="sub">Não feche esta tela.</p>
        <Progresso fracao={gravando.fracao} texto={gravando.etapa} />
      </div>
    );
  }

  const ultimaAmostra = passo === 1 && !entrada && atual >= d.amostras.length - 1;

  /*
   * 258 · o cabeçalho compacto da OS (§9.1 da ideia do fluxo da OS).
   *
   * O assistente não tinha título nenhum — só a régua dos passos (U2): quem
   * chegava pelo «Receber amostras» da OS não via em que OS estava. Com a
   * OS escolhida (pelo link ou no passo do lote), o topo diz qual, de quem,
   * e leva de volta a ela.
   */
  const osDoTopo = d.osId ? {
    numero: osDoLink?.id === d.osId ? osDoLink.numero : (d.osNumero ?? '').split(' — ')[0],
    obra: osDoLink?.id === d.osId ? osDoLink.obra_nome : (d.osNumero ?? '').split(' — ')[1] ?? null,
  } : null;

  return (
    <div>
      {osDoTopo && (
        <header className="cabeca-os compacta cabeca-assistente">
          <div className="cabeca-os-id">
            <div className="cabeca-os-linha">
              <h1>Novo recebimento</h1>
            </div>
            <p className="cabeca-os-sub">
              OS <Link className="mono" to={`/os/${d.osId}`}>{osDoTopo.numero}</Link>
              {d.clienteNome && <> · {d.clienteNome}</>}
              {osDoTopo.obra && <> · {osDoTopo.obra}</>}
            </p>
          </div>
        </header>
      )}
      <ol className="passos">
        {PASSOS.map((p, i) => (
          <li
            key={p}
            className={i < passo ? 'feito' : undefined}
            aria-current={i === passo ? 'step' : undefined}
          >
            {i + 1} · {p}
          </li>
        ))}
      </ol>

      <div ref={erroRef} tabIndex={-1}><ErroDaApi erro={erro} /></div>

      {osDivergente && (
        <div className="aviso atencao" role="alert">
          <strong>Este rascunho é de outra ordem de serviço.</strong>
          Você chegou pelo link de uma OS, mas há um recebimento a meio
          caminho — <b>{d.osNumero ?? 'sem número'}</b> — e ele não foi
          descartado, para não perder o que já estava digitado.
          <div style={{ marginTop: '.45rem' }}>
            <button
              type="button" className="botao secundario estreito"
              onClick={async () => {
                /*
                 * Começar do zero é ato explícito, e leva o rascunho
                 * junto: deixá-lo no armazenamento faria a próxima
                 * abertura ressuscitar o mesmo estado.
                 */
                try { localStorage.removeItem(CHAVE_RASCUNHO); } catch { /* nada */ }
                /*
                 * Limpa E TRAZ a OS do link. Só limpar deixaria o
                 * formulário em branco sem a OS, porque o efeito de
                 * preenchimento não roda de novo — trocando um beco por
                 * outro. Se a OS do link ainda não chegou (rede lenta,
                 * clique rápido), busca agora em vez de abrir em branco.
                 */
                const os = osDoLink ?? (osDoEndereco
                  ? await api<NonNullable<typeof osDoLink>>(`/os/${osDoEndereco}`)
                    .catch(() => null)
                  : null);
                if (os && !osDoLink) setOsDoLink(os);
                setD(os
                  ? { ...rascunhoVazio(), ...aplicarOsDoLink(os) }
                  : rascunhoVazio());
                setFotos([]);
                setReclamacoes([]);
                setErro(null);
                setPasso(0);
                setAtual(0);
              }}
            >
              Começar do zero com a OS do link
            </button>
          </div>
        </div>
      )}

      {reclamacoes.length > 0 && (
        <div className="aviso atencao" role="alert" ref={avisoRef} tabIndex={-1}>
          <strong>Falta preencher:</strong>
          <ul>
            {reclamacoes.map((r, i) => (
              <li key={i}>
                {r.campo
                  ? (
                    <button type="button" className="como-link"
                      onClick={() => irAoCampo(r.campo!)}>
                      {r.texto}
                    </button>
                  )
                  : r.texto}
              </li>
            ))}
          </ul>
        </div>
      )}

      {passo === 0 && <PassoLote d={d} mudar={mudar} emFalta={emFalta} />}

      {passo === 1 && entrada && (
        <ChegadaDasAmostras
          d={d}
          disponiveis={disponiveis}
          entrada={entrada}
          fotos={fotos}
          emFalta={emFalta}
          aoMudarEntrada={setEntrada}
          aoEscolherChegada={(c) => mudar('chegada', c)}
        />
      )}

      {passo === 1 && !entrada && (
        <PassoAmostra
          d={d}
          indice={atual}
          previstas={previstas}
          fotos={fotos}
          setFotos={setFotos}
          aoVerPrevistas={disponiveis.length ? abrirEntrada : undefined}
          aoIr={(i) => { setReclamacoes([]); setAtual(i); window.scrollTo(0, 0); }}
          aoMudar={(m) => setD((r) => ({
            ...r,
            amostras: r.amostras.map((a, i) => (i === atual ? { ...a, ...m } : a)),
          }))}
          aoMudarHerdado={(m) => setD((r) => ({ ...r, herdado: { ...r.herdado, ...m } }))}
          aoRemover={() => removerAmostra(atual)}
          furos={furosDoLote}
          semColeta={semColeta}
        />
      )}

      {passo === 2 && (
        <>
          <PassoDecisao
            d={d} mudar={mudar} fotos={fotos.length} semMinimo={semMinimo}
            emFalta={emFalta} previstas={previstas} listaDeFotos={fotos}
            aoAbrirAmostra={(i) => irPara(1, i)}
            aoMudarHerdado={(m) => setD((r) => ({ ...r, herdado: { ...r.herdado, ...m } }))}
          />
          {/*
            * 269: as fotos do lote moram no «Fechar», fechadas. São todas
            * opcionais — as obrigatórias já foram pedidas em cada amostra —
            * e um passo inteiro para elas era um passo que quase todos
            * pulavam (achado 8 da ideia do recebimento no celular).
            */}
          <details className="cartao fotos-do-lote-fechar">
            <summary>
              <b>Fotos do lote</b>{' '}
              <span className="sub">
                · opcionais{fotos.some((f) => f.amostraIndice === null)
                  ? ` · ${plural(fotos.filter((f) => f.amostraIndice === null).length, 'foto', 'fotos')}`
                  : ''}
              </span>
            </summary>
            <p className="sub">
              O que não é de uma amostra só: as amostras em conjunto, o veículo ou a
              transportadora que trouxe, o recibo de entrega. As fotos só sobem no fim e
              <b> não</b> entram no rascunho automático.
            </p>
            <FotosDoLote fotos={fotos} aoMudar={setFotos} />
          </details>
        </>
      )}

      {/*
        * E a resposta também aparece ONDE O DEDO ESTÁ.
        *
        * Rolar até o aviso conserta o caso de quem olha para a tela. Mas
        * a barra de ações é fixa no rodapé, e quem apertou está olhando
        * para ela — então ela mesma diz que houve recusa, e leva ao
        * aviso com um toque. Duas respostas para o mesmo clique é o
        * preço de o botão viver longe do formulário que ele valida.
        */}
      {reclamacoes.length > 0 && (
        <div className="recusa-do-rodape" role="alert">
          {/*
            * A recusa DIZ o que falta, aqui embaixo.
            *
            * A primeira versão disto era um botão com a contagem — «2
            * campos obrigatórios sem preencher, toque para ver». Melhor
            * que o silêncio, e ainda insuficiente: quem está com a caixa
            * na mão precisa ler O QUE falta, não descobrir que falta
            * algo e ir procurar. A Testesolo voltou a esbarrar depois da
            * primeira correção, e foi isso que faltou.
            *
            * O aviso do topo continua existindo — é ele que tem o foco
            * e que o leitor de tela anuncia. Este é o mesmo texto, no
            * lugar onde o dedo está.
            */}
          <strong>Falta preencher:</strong>
          <ul>
            {reclamacoes.slice(0, 4).map((r, i) => (
              <li key={i}>
                {r.campo
                  ? (
                    <button type="button" className="como-link"
                      onClick={() => irAoCampo(r.campo!)}>
                      {r.texto}
                    </button>
                  )
                  : r.texto}
              </li>
            ))}
            {reclamacoes.length > 4 && (
              <li>e mais {reclamacoes.length - 4}.</li>
            )}
          </ul>
          <button
            type="button"
            className="como-link"
            onClick={() => {
              avisoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              avisoRef.current?.focus();
            }}
          >
            ir para o começo do formulário
          </button>
        </div>
      )}

      <div className="acoes-fixas">
        {(passo > 0 || atual > 0) && (
          <button type="button" className="botao secundario" onClick={voltar}>
            {passo === 1 && !entrada && atual > 0 ? 'Amostra anterior' : 'Voltar'}
          </button>
        )}
        {passo === 1 && ultimaAmostra && (
          <button
            type="button" className="botao secundario"
            onClick={() => acrescentar(d.amostras[atual])}
          >
            + Mais uma
          </button>
        )}
        {passo < PASSOS.length - 1
          ? (
            <button type="button" className="botao" onClick={avancar}>
              {passo === 1 && !entrada && !ultimaAmostra ? 'Próxima amostra' : 'Próximo'}
            </button>
          )
          : <button type="button" className="botao" onClick={gravar}>Registrar recebimento</button>}
      </div>
    </div>
  );
}

// ── Passo 1 · Lote ───────────────────────────────────────────────────

function PassoLote({
  d, mudar, emFalta,
}: {
  d: Rascunho;
  mudar<K extends keyof Rascunho>(c: K, v: Rascunho[K]): void;
  emFalta: string | null;
}) {
  const { eu } = useSessao();

  return (
    <>
      <div className="cartao">
        <h2>De quem é o material</h2>
        <BuscaCliente
          clienteId={d.clienteId}
          clienteNome={d.clienteNome}
          travado={!!d.osId}
          aoEscolher={(c) => {
            mudar('clienteId', c?.id ?? null);
            mudar('clienteNome', c?.razao_social ?? null);
          }}
        />
        <BuscaOs
          osId={d.osId}
          osNumero={d.osNumero}
          aoEscolher={(os) => {
            mudar('osId', os?.id ?? null);
            mudar('osNumero', os ? `${os.numero} — ${os.obra_nome ?? ''}` : null);
            // Escolher a OS define o cliente: o banco recusa lote cujo
            // cliente não é o da OS (migração 0017), e descobrir isso só
            // na gravação seria descobrir tarde.
            if (os) {
              mudar('clienteId', os.cliente_id);
              mudar('clienteNome', os.cliente);
            }
          }}
        />
        <div data-campo="origem"
          className={emFalta === 'origem' ? 'em-falta' : undefined}>
          <Escolha
            rotulo="Origem"
            opcoes={ORIGENS}
            valor={d.origem}
            erro={emFalta === 'origem'
              ? 'Escolha de onde o material veio.' : undefined}
            aoEscolher={(v) => mudar('origem', v ?? '')}
          />
        </div>
      </div>

      <div className="cartao">
        <h2>Quem entregou e quem recebeu</h2>
        {/*
          * «Entregue por» é obrigatório e muda de forma com a origem
          * (04/09/2026): equipe própria escolhe uma equipe cadastrada;
          * cliente, empresa de sondagem e controle da obra escrevem o
          * nome completo da pessoa e a empresa.
          */}
        <div data-campo="entregue-por"
          className={emFalta === 'entregue-por' ? 'em-falta' : undefined}>
          {!d.origem ? (
            <p className="sub">Escolha a origem acima para dizer quem entregou.</p>
          ) : d.origem === 'equipe_propria' ? (
            <EquipeQueEntregou
              valor={d.entregaEquipeId}
              erro={emFalta === 'entregue-por' ? 'Escolha a equipe.' : undefined}
              aoEscolher={(id) => mudar('entregaEquipeId', id)} />
          ) : (
            <>
              <Campo
                rotulo="Nome completo de quem entregou"
                value={d.entregaNome}
                maxLength={200}
                autoComplete="off"
                placeholder="Como está no documento da pessoa"
                erro={emFalta === 'entregue-por' && !d.entregaNome.trim()
                  ? 'Obrigatório.' : undefined}
                onChange={(e) => mudar('entregaNome', e.target.value)}
              />
              <Campo
                rotulo="Empresa que entregou"
                value={d.entregaEmpresa}
                maxLength={200}
                autoComplete="off"
                placeholder={d.origem === 'cliente'
                  ? 'O cliente, ou a transportadora que ele mandou'
                  : 'A empresa de sondagem ou o controle da obra'}
                erro={emFalta === 'entregue-por' && !d.entregaEmpresa.trim()
                  ? 'Obrigatório.' : undefined}
                onChange={(e) => mudar('entregaEmpresa', e.target.value)}
              />
              <Campo
                rotulo="Documento de quem entregou"
                value={d.entregaDoc}
                maxLength={40}
                autoComplete="off"
                dica="Opcional. Vai impresso no relatório de recebimento."
                onChange={(e) => mudar('entregaDoc', e.target.value)}
              />
            </>
          )}
        </div>

        <RecebidoPor nome={eu?.nome ?? null} temFicha={!!eu?.funcionarioId} />

        <Campo
          rotulo="Data e hora do recebimento"
          type="datetime-local"
          value={d.datahora}
          onChange={(e) => mudar('datahora', e.target.value)}
          dica="Já vem preenchida com agora. Ajuste se o material chegou antes."
        />
      </div>
    </>
  );
}

function BuscaCliente({
  clienteId, clienteNome, travado, aoEscolher,
}: {
  clienteId: string | null; clienteNome: string | null; travado: boolean;
  aoEscolher(c: Cliente | null): void;
}) {
  const rota = useCallback(
    (alvo: string) => `/clientes?busca=${encodeURIComponent(alvo)}`, []);
  const { texto, setTexto, achados, semPermissao } = useBusca<Cliente>(rota);
  const [cadastrando, setCadastrando] = useState(false);
  const { pode } = useSessao();
  const podeCadastrar = pode('cadastro.editar');
  const vazio = !!achados && achados.length === 0
    && texto.trim().length >= 2 && !semPermissao;

  if (clienteId) {
    return (
      <div className="campo">
        <span className="rotulo">Cliente</span>
        <div className="linha">
          <div className="item" style={{ cursor: 'default' }}>
            <span className="cod">{clienteNome}</span>
          </div>
          {!travado && (
            <button
              type="button" className="botao secundario estreito"
              style={{ flex: '0 0 auto' }}
              onClick={() => { aoEscolher(null); setTexto(''); }}
            >
              Trocar
            </button>
          )}
        </div>
        {travado && (
          <span className="dica">Vem da OS escolhida. Troque a OS para mudar o cliente.</span>
        )}
      </div>
    );
  }

  return (
    <div className="campo">
      <span className="rotulo">Cliente</span>
      <input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Razão social ou CNPJ"
        autoComplete="off"
        aria-label="Buscar cliente"
      />
      <span className="dica">
        Obrigatório, mesmo sem OS. Amostra sem dono é amostra que ninguém sabe
        para quem ligar no dia seguinte.
      </span>
      {semPermissao && <span className="dica">Seu perfil não consulta clientes.</span>}
      {achados && achados.length > 0 && (
        <div className="lista" style={{ marginTop: '.5rem' }}>
          {achados.map((c) => (
            <button key={c.id} type="button" className="item" onClick={() => aoEscolher(c)}>
              <span className="cod">{c.razao_social}</span>
              {c.cnpj_cpf && <span className="meta">{c.cnpj_cpf}</span>}
            </button>
          ))}
        </div>
      )}
      {vazio && (
        <div className="saida-do-beco">
          <span className="dica">
            Nenhum cliente cadastrado com “{texto.trim()}”.
          </span>
          {podeCadastrar ? (
            <button
              type="button" className="botao secundario"
              onClick={() => setCadastrando(true)}
            >
              Cadastrar “{texto.trim()}”
            </button>
          ) : (
            <span className="dica">
              Seu perfil não cadastra clientes. Peça a quem administra os
              cadastros para incluir este, ou escolha um cliente já existente
              — o recebimento não pode ser gravado sem dono.
            </span>
          )}
        </div>
      )}

      <CadastroRapidoCliente
        aberto={cadastrando}
        nomeDigitado={texto.trim()}
        aoFechar={() => setCadastrando(false)}
        aoCriar={(c) => { setCadastrando(false); aoEscolher(c); }}
      />
    </div>
  );
}

/**
 * Busca de OS — que abre a lista antes de alguém digitar.
 *
 * «Quando clicamos no campo ordem de serviço no módulo de recebimento de
 *  amostras, abra um dropdown das últimas OS geradas, e ao escrever parte
 *  da numeração a opção correta já vai se afunilando automaticamente.
 *  Assim o usuário não precisa ter decorado o número exato da OS»
 * — Testesolo, 02/09/2026.
 *
 * Duas mudanças pequenas e uma razão só. O piso da busca caiu de dois
 * caracteres para ZERO: com zero, o campo vazio já é uma consulta, e a
 * rota devolve as OS em ordem de criação (`ORDER BY criado_em DESC`) —
 * as últimas geradas são as primeiras da lista. E a lista aparece ao
 * FOCAR, não só ao digitar.
 *
 * Quem sabe o número continua digitando e a lista afunila; quem não sabe
 * agora tem o que olhar. Antes, campo vazio mostrava nada, e o único
 * caminho era lembrar.
 */
function BuscaOs({
  osId, osNumero, aoEscolher,
}: {
  osId: string | null; osNumero: string | null;
  aoEscolher(os: OrdemServico | null): void;
}) {
  const rota = useCallback((alvo: string) => `/os?busca=${encodeURIComponent(alvo)}`, []);
  const { texto, setTexto, achados, semPermissao } = useBusca<OrdemServico>(rota, 0);
  const [focado, setFocado] = useState(false);

  if (osId) {
    return (
      <div className="campo">
        <span className="rotulo">Ordem de serviço</span>
        <div className="linha">
          <div className="item" style={{ cursor: 'default' }}>
            <span className="cod">{osNumero}</span>
          </div>
          <button
            type="button" className="botao secundario estreito"
            style={{ flex: '0 0 auto' }}
            onClick={() => { aoEscolher(null); setTexto(''); }}
          >
            Trocar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campo">
      <span className="rotulo">Ordem de serviço</span>
      <input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onFocus={() => setFocado(true)}
        /*
         * O atraso ao sair é para o clique na opção acontecer ANTES de a
         * lista sumir: `blur` dispara primeiro, e sem ele o item some
         * debaixo do dedo.
         */
        onBlur={() => setTimeout(() => setFocado(false), 200)}
        placeholder="Número da obra (7 dígitos), obra ou cliente"
        inputMode="numeric"
        autoComplete="off"
        aria-label="Buscar ordem de serviço"
      />
      <span className="dica">
        Opcional. Sem OS o recebimento fica marcado como <b>órfão</b> na lista, e
        nenhum ensaio pode começar até alguém atribuir a OS.
      </span>
      {semPermissao && (
        <span className="dica">Seu perfil não consulta ordens de serviço.</span>
      )}
      {focado && achados && achados.length > 0 && (
        <>
          {!texto.trim() && (
            <span className="dica">Últimas ordens de serviço abertas:</span>
          )}
          <div className="lista" style={{ marginTop: '.5rem' }}>
            {achados.map((o) => (
              <button key={o.id} type="button" className="item"
                      onClick={() => aoEscolher(o)}>
                <span className="cod">{o.numero}</span>
                <span className="sub">{o.obra_nome}</span>
                <span className="meta">{o.cliente}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {focado && achados && achados.length === 0 && texto.trim().length >= 2
        && !semPermissao && (
        <span className="dica">Nenhuma OS encontrada para “{texto.trim()}”.</span>
      )}
    </div>
  );
}

/**
 * O que a previsão já sabe entra na amostra ao amarrar (UX-016).
 *
 * «Amarrar a amostra à previsão traz os ensaios, mas não traz furo,
 *  profundidade nem identificação — tudo é redigitado» (doc 137). A
 * previsão guarda «SP-01 / 1,00 m» e «SP-01»; o técnico, de luva,
 * redigitava os três — e «1,00 na amostra de 2,00 m» é o erro mais caro
 * do recebimento. Só preenche o que está VAZIO: o que a pessoa já
 * escreveu vale mais que o previsto, e continua editável depois.
 *
 * A profundidade sai da identificação prevista quando ela traz números
 * com «m»: «1,00 m», «1,00 – 2,00 m», «1,00 a 1,45 m». Sem esse
 * padrão, fica em branco — não se adivinha profundidade.
 */
function daPrevisao(
  id: string | null, previstas: AmostraPrevista[], a: LinhaAmostra,
): Partial<LinhaAmostra> {
  if (!id) return {};
  const p = previstas.find((x) => x.id === id);
  if (!p) return {};
  const m: Partial<LinhaAmostra> = {};
  if (!a.identificacaoRecebida.trim() && p.identificacao_prevista) {
    m.identificacaoRecebida = p.identificacao_prevista;
  }
  if (!a.pontoInvestigacao.trim() && p.origem_prevista) {
    m.pontoInvestigacao = p.origem_prevista;
  }
  /*
   * 0188: a previsão pode trazer o tipo e a profundidade em coluna
   * própria (relação colada). Quando traz, é sugestão — o que está no
   * saco vale mais, e por isso só preenche campo vazio.
   */
  if (!a.tipoAmostra && p.tipo_amostra) m.tipoAmostra = p.tipo_amostra;
  if (!a.profundidadeInicial.trim() && p.profundidade_inicial) {
    m.profundidadeInicial = String(p.profundidade_inicial).replace('.', ',');
  }
  if (!a.profundidadeFinal.trim() && p.profundidade_final) {
    m.profundidadeFinal = String(p.profundidade_final).replace('.', ',');
  }
  const texto = p.identificacao_prevista ?? '';
  const numeros = [...texto.matchAll(/(\d+(?:[.,]\d+)?)\s*(?:m\b|–|-|a\b)/g)]
    .map((x) => x[1].replace('.', ','));
  const comM = /\d\s*m\b/.test(texto);
  if (comM && numeros.length >= 1 && !a.profundidadeInicial.trim()) {
    m.profundidadeInicial = numeros[0];
  }
  if (comM && numeros.length >= 2 && !a.profundidadeFinal.trim()) {
    m.profundidadeFinal = numeros[1];
  }
  return m;
}

// ── Passo 2 · Uma amostra por tela ───────────────────────────────────

// ── Passo 2, a entrada · Como as amostras chegaram? (onda 251) ───────

/**
 * A entrada do passo das amostras, quando a OS tem prevista por chegar.
 *
 * Duas saídas (decisão do Humberto, 02/10/2026 — «em recebimento não há
 * necessidade de gerar série por furo»):
 *
 *  · **Marcar as previstas da OS** — a tabela das previstas por chegar,
 *    com a nomenclatura que a OS deu, o furo, a profundidade, o tipo e os
 *    ensaios marcados. Quem recebe marca as que vieram; cada uma vira
 *    amostra ligada à prevista e herda os ensaios. A que não veio
 *    continua por chegar — e a conferência do fim diz quais;
 *  · **Uma a uma** — o caminho de sempre, para o que chegou sem previsão;
 *    o nome digitado que casar com uma prevista vira sugestão de ligação.
 *
 * O que chegou e não estava previsto entra pela mesma tabela («+ Chegou
 * amostra que não estava prevista»): ele não consome escopo agora — nasce
 * sem ensaio e é programado depois, na OS.
 */
function ChegadaDasAmostras({
  d, disponiveis, entrada, fotos, emFalta, aoMudarEntrada, aoEscolherChegada,
}: {
  d: Rascunho;
  disponiveis: AmostraPrevista[];
  entrada: Entrada;
  fotos: FotoPendente[];
  emFalta: string | null;
  aoMudarEntrada(e: Entrada): void;
  aoEscolherChegada(c: Rascunho['chegada']): void;
}) {
  const n = disponiveis.length;
  const marcada = (id: string) => entrada.marcadas.includes(id);
  const todas = n > 0 && disponiveis.every((p) => marcada(p.id));
  const alternar = (id: string) => aoMudarEntrada({
    ...entrada,
    marcadas: marcada(id)
      ? entrada.marcadas.filter((x) => x !== id)
      : [...entrada.marcadas, id],
  });
  const mantidas = entrada.manter
    .map((chave) => {
      const i = d.amostras.findIndex((a) => a.chave === chave);
      return i < 0 ? null : { a: d.amostras[i], fotos: fotos.filter((f) => f.amostraIndice === i).length };
    })
    .filter((x): x is { a: LinhaAmostra; fotos: number } => !!x);
  const total = entrada.marcadas.length + mantidas.length + entrada.novas;
  const numero = d.osNumero?.split(' — ')[0] ?? '';

  return (
    <>
      <div className={`cartao chegada-das-amostras${emFalta === 'chegada' ? ' em-falta' : ''}`}
        data-campo="chegada">
        <h2>Como as amostras chegaram?</h2>
        <p className="sub">
          A OS <span className="mono">{numero}</span> tem{' '}
          {plural(n, 'amostra prevista que ainda não chegou', 'amostras previstas que ainda não chegaram')}.
        </p>
        <Escolha
          rotulo="Escolha o caminho"
          classe="chegada"
          limpavel={false}
          valor={d.chegada}
          aoEscolher={(v) => aoEscolherChegada((v ?? '') as Rascunho['chegada'])}
          opcoes={[
            { valor: 'previstas', texto: 'Marcar as previstas da OS',
              detalhe: `${n} por chegar · a nomenclatura prevista já vem preenchida, `
                + 'com furo, profundidade e ensaios' },
            { valor: 'uma', texto: 'Uma a uma',
              detalhe: 'para o que chegou sem previsão — amostra por amostra; o nome '
                + 'que casar com uma prevista vira sugestão' },
          ]}
        />
      </div>

      {d.chegada === 'previstas' && (
        <div className="cartao">
          <div className="linha-topo">
            <h2 className="cresce">Previstas da OS que ainda não chegaram</h2>
            <button
              type="button" className="botao discreto estreito"
              onClick={() => aoMudarEntrada({
                ...entrada, marcadas: todas ? [] : disponiveis.map((p) => p.id),
              })}
            >
              {todas ? 'Desmarcar todas' : 'Marcar todas'}
            </button>
          </div>
          <p className="sub">
            A nomenclatura prevista já está aqui: marque as que vieram nesta
            entrega. Elas viram recebidas com o nome e os ensaios que já
            tinham; as outras continuam por chegar.
          </p>
          <div className="rolagem">
            <table className="dados previstas-que-chegam">
              <thead>
                <tr>
                  <th><span className="so-leitor">Chegou</span></th>
                  <th>Prevista</th><th>Furo/ponto</th><th>Profundidade (m)</th>
                  <th>Tipo</th><th>Ensaios que ela traz</th><th>Ao gravar</th>
                </tr>
              </thead>
              <tbody>
                {disponiveis.map((p) => {
                  const on = marcada(p.id);
                  const nome = nomeDaPrevista(p);
                  return (
                    <tr key={p.id} className={on ? 'marcada' : undefined}>
                      <td>
                        <input
                          type="checkbox" checked={on} onChange={() => alternar(p.id)}
                          aria-label={`Chegou ${nome}`}
                        />
                      </td>
                      <td>
                        <b>{nome}</b>
                        {p.identificacao_cliente && p.identificacao_cliente !== nome && (
                          <small className="det">do cliente: {p.identificacao_cliente}</small>
                        )}
                      </td>
                      <td>{p.origem_prevista || <span className="nada">—</span>}</td>
                      <td className="num">
                        {faixaProfundidade(
                          p.profundidade_inicial == null ? undefined : Number(p.profundidade_inicial),
                          p.profundidade_final == null ? undefined : Number(p.profundidade_final))}
                      </td>
                      <td>{rotulo(p.tipo_amostra ?? '') || <span className="nada">—</span>}</td>
                      <td>{ensaiosDaPrevista(p) || <span className="nada">nenhum ensaio marcado</span>}</td>
                      <td>
                        {on
                          ? <span className="selo ok">Vira recebida</span>
                          : <span className="nada">continua por chegar</span>}
                      </td>
                    </tr>
                  );
                })}
                {mantidas.map(({ a, fotos: nf }) => (
                  <tr key={a.chave} className="marcada">
                    <td>
                      <input type="checkbox" checked disabled aria-label="Amostra sem previsão" />
                    </td>
                    <td>
                      <b>{a.identificacaoRecebida || 'Amostra sem nome'}</b>
                      <small className="det">
                        já digitada{nf ? ` · ${plural(nf, 'foto', 'fotos')}` : ''}
                      </small>
                    </td>
                    <td>{a.pontoInvestigacao || <span className="nada">—</span>}</td>
                    <td className="num">
                      {faixaProfundidade(paraNumero(a.profundidadeInicial), paraNumero(a.profundidadeFinal))}
                    </td>
                    <td>{rotulo(a.tipoAmostra) || <span className="nada">—</span>}</td>
                    <td><span className="selo atencao">Sem previsão</span></td>
                    <td>
                      <button
                        type="button" className="como-link"
                        onClick={() => aoMudarEntrada({
                          ...entrada, manter: entrada.manter.filter((c) => c !== a.chave),
                        })}
                      >
                        Tirar
                      </button>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: entrada.novas }, (_, i) => (
                  <tr key={`nova-${i}`} className="marcada">
                    <td>
                      <input type="checkbox" checked disabled aria-label="Amostra sem previsão" />
                    </td>
                    <td>
                      <b>Amostra sem previsão {mantidas.length + i + 1}</b>
                      <small className="det">o nome você digita na tela dela</small>
                    </td>
                    <td colSpan={3}><span className="nada">—</span></td>
                    <td><span className="selo atencao">Sem previsão</span></td>
                    <td>
                      <button
                        type="button" className="como-link"
                        onClick={() => aoMudarEntrada({ ...entrada, novas: entrada.novas - 1 })}
                      >
                        Tirar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="linha chegada-rodape">
            <button
              type="button" className="como-link"
              onClick={() => aoMudarEntrada({ ...entrada, novas: entrada.novas + 1 })}
            >
              + Chegou amostra que não estava prevista
            </button>
            <span className="sub">
              {plural(total, 'amostra', 'amostras')} nesta entrega ·{' '}
              {entrada.marcadas.length} das previstas
              {mantidas.length + entrada.novas > 0
                ? ` · ${mantidas.length + entrada.novas} sem previsão` : ''}
            </span>
          </div>
          <p className="sub">
            A amostra sem previsão não consome escopo agora: ela entra sem
            ensaio e é programada depois, na OS.
          </p>
        </div>
      )}

      {d.chegada === 'uma' && (
        <div className="aviso info">
          Amostra por amostra, com as fotos obrigatórias em cada uma. O nome
          digitado que casar com uma prevista da OS vira <b>sugestão de
          ligação</b> — e você confirma.
        </div>
      )}
    </>
  );
}

// ── Passo 2 · Uma amostra por tela ───────────────────────────────────

interface PassoAmostraProps {
  d: Rascunho;
  indice: number;
  previstas: AmostraPrevista[];
  fotos: FotoPendente[];
  setFotos(f: FotoPendente[]): void;
  /** Volta à entrada «Como as amostras chegaram?» (onda 251). */
  aoVerPrevistas?: () => void;
  aoIr(i: number): void;
  aoMudar(m: Partial<LinhaAmostra>): void;
  aoMudarHerdado(m: Partial<Herdado>): void;
  aoRemover(): void;
  /** 269: os furos que se escolhem por botão. */
  furos: string[];
  /** 348: os pontos da OS que não coletam amostra (código em minúsculas → tipo). */
  semColeta: Map<string, string>;
}

function PassoAmostra({
  d, indice, previstas, fotos, setFotos, aoVerPrevistas, aoIr, aoMudar, aoMudarHerdado, aoRemover,
  furos,
  semColeta,
}: PassoAmostraProps) {
  const a = d.amostras[indice];
  if (!a) return <p className="vazio">Nenhuma amostra.</p>;

  const acond = a.tipoAmostra ? ACONDICIONAMENTO[a.tipoAmostra] ?? [] : [];
  const jaAmarradas = new Set(
    d.amostras.filter((_, i) => i !== indice).map((x) => x.osAmostraPrevistaId).filter(Boolean));
  const disponiveis = porChegar(previstas).filter((p) => !jaAmarradas.has(p.id));
  const ligada = a.osAmostraPrevistaId
    ? previstas.find((p) => p.id === a.osAmostraPrevistaId) ?? null
    : null;

  /*
   * A sugestão pelo nome (onda 251, `RN-220`; D5 da ideia do fluxo da OS).
   *
   * O nome digitado que casa com o de uma prevista por chegar — pela
   * mesma chave da programação (`chaveDoNome`: «SP-04/3», «sp 04 3» e
   * «SP04-03» são o mesmo nome) — vira PERGUNTA, nunca ligação
   * automática: quem está com o saco na mão é quem sabe se é aquela.
   */
  const chave = chaveDoNome(a.identificacaoRecebida);
  const sugerida = !ligada && chave
    ? disponiveis.find((p) => p.id !== a.sugestaoRecusada
      && (chaveDoNome(p.identificacao_prevista) === chave
        || chaveDoNome(p.identificacao_cliente) === chave)) ?? null
    : null;

  return (
    <>
      {/* A trilha de amostras é o mapa: mostra onde o técnico está, quantas
          faltam e quais ainda não têm as duas fotos. */}
      {aoVerPrevistas && (
        <button type="button" className="como-link ver-previstas" onClick={aoVerPrevistas}>
          ← Previstas da OS · como as amostras chegaram
        </button>
      )}
      <div className="trilha-amostras" role="tablist" aria-label="Amostras do lote">
        {d.amostras.map((x, i) => {
          const ok = minimoCumprido(fotos, i);
          return (
            <button
              key={x.chave}
              type="button"
              role="tab"
              aria-selected={i === indice}
              className={`passo-amostra${i === indice ? ' atual' : ''}${ok ? ' ok' : ''}`}
              onClick={() => aoIr(i)}
              title={x.identificacaoRecebida || `Amostra ${i + 1}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      <div className="cartao">
        <div className="linha-topo">
          <h2 className="cresce">
            Amostra {indice + 1} <span className="sub">de {d.amostras.length}</span>
          </h2>
          {d.amostras.length > 1 && (
            <button
              type="button" className="botao discreto estreito"
              style={{ minHeight: 40, fontSize: '.85rem' }}
              onClick={aoRemover}
            >
              Remover
            </button>
          )}
        </div>

        {/* De onde a amostra vem — a mesma palavra da conferência e da
            entrada: «Prevista na OS» ou «Sem previsão» (onda 251). */}
        {(ligada || previstas.length > 0) && (
          <p className="origem-da-amostra">
            {ligada
              ? (
                <>
                  <span className="selo ok">Prevista na OS</span>{' '}
                  <b>{nomeDaPrevista(ligada)}</b>
                  {' · '}{ensaiosDaPrevista(ligada) ? `traz ${ensaiosDaPrevista(ligada)}` : 'sem ensaio marcado'}
                </>
              )
              : (
                <>
                  <span className="selo atencao">Sem previsão</span>{' '}
                  entra sem ensaio — programe depois, na OS
                </>
              )}
          </p>
        )}

        {(disponiveis.length > 0 || ligada) && (
          <Escolha
            rotulo="Prevista da OS"
            opcoes={[...(ligada && !disponiveis.some((p) => p.id === ligada.id) ? [ligada] : []),
              ...disponiveis].map((p) => ({
              valor: p.id,
              texto: [p.identificacao_prevista, p.origem_prevista].filter(Boolean).join(' · ')
                || `Prevista ${p.ordem}`,
            }))}
            valor={a.osAmostraPrevistaId}
            aoEscolher={(v) => aoMudar({ osAmostraPrevistaId: v, ...daPrevisao(v, previstas, a) })}
            dica="Ligar à prevista traz junto os ensaios que a OS marcou nela — e preenche identificação, furo e profundidade com o que foi previsto (você pode corrigir). Toque de novo para desligar."
          />
        )}

        <Campo
          rotulo="Identificação recebida"
          value={a.identificacaoRecebida}
          maxLength={120}
          autoComplete="off"
          placeholder="Como veio escrito na etiqueta original"
          onChange={(e) => aoMudar({ identificacaoRecebida: e.target.value })}
        />

        {sugerida && (
          <div className="aviso info sugestao-prevista" role="status">
            <span>
              Parece a prevista <b>{nomeDaPrevista(sugerida)}</b>
              {sugerida.origem_prevista ? ` (${sugerida.origem_prevista})` : ''} da OS.
              {' '}Ligar a ela traz {ensaiosDaPrevista(sugerida)
                ? <>os ensaios marcados — <b>{ensaiosDaPrevista(sugerida)}</b></>
                : 'a previsão'} e preenche o que estiver vazio.
            </span>
            <div className="linha">
              <button
                type="button" className="botao secundario estreito"
                onClick={() => aoMudar({
                  osAmostraPrevistaId: sugerida.id, ...daPrevisao(sugerida.id, previstas, a),
                })}
              >
                Ligar a esta prevista
              </button>
              <button
                type="button" className="botao discreto estreito"
                onClick={() => aoMudar({ sugestaoRecusada: sugerida.id })}
              >
                Não é esta
              </button>
            </div>
          </div>
        )}

        <Escolha
          rotulo="Tipo de amostra"
          opcoes={OPCOES_TIPO}
          valor={a.tipoAmostra}
          aoEscolher={(v) => aoMudar({
            tipoAmostra: v ?? '',
            // Trocar o tipo invalida o acondicionamento: saco de ráfia em
            // indeformada é recusado pelo banco.
            tipoAcondicionamento: '',
            acondicionamentoDescricao: '',
          })}
        />

        {a.tipoAmostra && (
          <Escolha
            rotulo={rotuloDoAcondicionamento(a.tipoAmostra)}
            opcoes={acond.map((v) => ({ valor: v, texto: rotulo(v) }))}
            valor={a.tipoAcondicionamento}
            aoEscolher={(v) => aoMudar({
              tipoAcondicionamento: v ?? '',
              acondicionamentoDescricao: v === 'outro' ? a.acondicionamentoDescricao : '',
            })}
          />
        )}

        {a.tipoAcondicionamento === 'outro' && (
          <Campo
            rotulo="O que é"
            value={a.acondicionamentoDescricao}
            maxLength={200}
            autoComplete="off"
            placeholder="tubo de PVC, cilindro de CBR, lascas, balde…"
            dica="Obrigatório: um acervo cheio de “outro” não descreve nada."
            onChange={(e) => aoMudar({ acondicionamentoDescricao: e.target.value })}
          />
        )}

        <div className="linha">
          <Campo
            rotulo="Profundidade inicial (m)"
            inputMode="decimal"
            value={a.profundidadeInicial}
            autoComplete="off"
            placeholder="1,00"
            onChange={(e) => aoMudar({ profundidadeInicial: e.target.value })}
          />
          <Campo
            rotulo="Profundidade final (m)"
            inputMode="decimal"
            value={a.profundidadeFinal}
            autoComplete="off"
            placeholder="1,45"
            onChange={(e) => aoMudar({ profundidadeFinal: e.target.value })}
          />
        </div>

        {/*
          * 269 (`RN-226`): o furo é sempre da amostra, e se ESCOLHE — os
          * pontos da OS (módulo de campo), os furos das previstas e os já
          * usados neste lote viram botões; digitar fica para o ponto que
          * ainda não existe. Antes vinha do lote em silêncio para toda
          * amostra que ficasse em branco.
          */}
        {furos.length > 0 && (
          <div className="furos-botoes" role="group" aria-label="Furo / ponto desta amostra"
            data-campo={`furo-${indice}`}>
            <span className="rotulo">Furo / ponto</span>
            <div className="chips-furo">
              {furos.map((f) => (
                <button key={f} type="button" className="chip-furo"
                  aria-pressed={a.pontoInvestigacao.trim() === f}
                  onClick={() => aoMudar({
                    pontoInvestigacao: a.pontoInvestigacao.trim() === f ? '' : f })}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="linha">
          <Campo
            rotulo={furos.length > 0 ? 'Outro furo / ponto' : 'Furo / ponto'}
            value={furos.includes(a.pontoInvestigacao.trim()) ? '' : a.pontoInvestigacao}
            maxLength={120}
            autoComplete="off"
            placeholder="SP-01"
            dica={semColeta.has(a.pontoInvestigacao.trim().toLocaleLowerCase('pt-BR'))
              ? `«${a.pontoInvestigacao.trim()}» é ${semColeta.get(a.pontoInvestigacao.trim().toLocaleLowerCase('pt-BR'))} desta OS e não coleta amostra — escolha outro furo.`
              : furos.length > 0
                ? 'Só se o furo não estiver nos botões acima.'
                : 'O furo é desta amostra — não vem de outra.'}
            onChange={(e) => aoMudar({ pontoInvestigacao: e.target.value })}
          />
          {/*
            * «Quantidade aproximada» saiu daqui em 04/09/2026: «pode
            * remover, pois é um parâmetro que não está sendo usado». A
            * coluna continua no banco (append-only) e no PDF quando há
            * valor antigo; o campo é que não se pede mais.
            */}
        </div>

        <Campo
          rotulo="Camada"
          value={a.camada}
          maxLength={200}
          autoComplete="off"
          placeholder="ex.: argila siltosa vermelha"
          onChange={(e) => aoMudar({ camada: e.target.value })}
        />

        <Area
          rotulo="Como este material chegou"
          value={a.condicoesEmbalagem}
          maxLength={1000}
          rows={2}
          placeholder="ex.: tubo Shelby com a ponta amassada; saco íntegro"
          dica="É desta amostra, não do pacote — é ela que vai para o ensaio."
          onChange={(e) => aoMudar({ condicoesEmbalagem: e.target.value })}
        />

        <Area
          rotulo="Observações"
          value={a.observacoes}
          maxLength={1000}
          rows={2}
          onChange={(e) => aoMudar({ observacoes: e.target.value })}
        />
      </div>

      <div className="cartao">
        <h2>Fotos desta amostra</h2>
        <p className="sub" style={{ marginBottom: '.7rem' }}>
          Toque no item e a câmera abre já sabendo o que a foto é.
          {' '}<b>{MINIMO.map((t) => rotulo(t).toLowerCase()).join(' e ')}</b> são
          obrigatórias — sem elas o recebimento não conclui.
        </p>
        <FotosDaAmostra fotos={fotos} aoMudar={setFotos} amostraIndice={indice} />
      </div>

      <details className="cartao">
        <summary><b>O que vale para todas as amostras</b></summary>
        <p className="sub" style={{ margin: '.6rem 0' }}>
          Digitado uma vez e herdado por cada amostra que deixar o campo em branco.
          O furo não está aqui: ele é sempre da amostra (`RN-226`).
        </p>
        <div className="linha">
          <Campo
            rotulo="Data da coleta"
            type="date"
            value={d.herdado.dataColeta}
            dica="Opcional — em branco quando não se sabe."
            onChange={(e) => aoMudarHerdado({ dataColeta: e.target.value })}
          />
        </div>
        {/*
          Detalhe DENTRO do local, não o local.

          O lugar em si é escolhido no passo da decisão, do cadastro
          (0062). Este campo é o complemento que o cadastro não tem —
          «nível 3, ao fundo» —, e o rótulo precisa deixar isso claro:
          dois campos que parecem perguntar a mesma coisa é como um
          acervo ganha «camara umida», «Câmara Úmida» e «CU».
        */}
        <Campo
          rotulo="Detalhe dentro do local"
          value={d.herdado.localizacaoFisica}
          maxLength={120}
          autoComplete="off"
          placeholder="Nível 3, ao fundo"
          dica="O lugar em si você escolhe no passo da decisão."
          onChange={(e) => aoMudarHerdado({ localizacaoFisica: e.target.value })}
        />
      </details>
    </>
  );
}

// ── Passo 3 · Fechar: decisão e conferência ─────────────────────────

/**
 * As amostras do lote agrupadas por furo (269, `RN-226`), na ordem em que
 * cada furo apareceu; «sem furo» no fim. Guarda o índice de cada uma — é
 * por ele que a lista volta à tela da amostra.
 */
function gruposPorFuro(amostras: LinhaAmostra[]): [string, { a: LinhaAmostra; i: number }[]][] {
  const m = new Map<string, { a: LinhaAmostra; i: number }[]>();
  amostras.forEach((a, i) => {
    const f = a.pontoInvestigacao.trim();
    if (!m.has(f)) m.set(f, []);
    m.get(f)!.push({ a, i });
  });
  const semFuro = m.get('');
  m.delete('');
  const lista = [...m.entries()];
  if (semFuro) lista.push(['', semFuro]);
  return lista;
}

function PassoDecisao({
  d, mudar, aoMudarHerdado, fotos, semMinimo, emFalta, previstas, listaDeFotos, aoAbrirAmostra,
}: {
  emFalta: string | null;
  previstas: AmostraPrevista[];
  /** 269: as fotos, para a lista dizer qual amostra está sem o mínimo. */
  listaDeFotos: FotoPendente[];
  /** 269: tocar na amostra da conferência volta à tela dela. */
  aoAbrirAmostra(i: number): void;
  d: Rascunho;
  mudar<K extends keyof Rascunho>(c: K, v: Rascunho[K]): void;
  aoMudarHerdado(m: Partial<Herdado>): void;
  fotos: number;
  semMinimo: number;
}) {
  /*
   * Onde o material foi posto — no passo da DECISÃO, e não escondido em
   * «o que vale para todas as amostras».
   *
   * A regra é «aceitou, diga onde pôs» (§6, doc 48), e a pergunta tem
   * que ficar ao lado do aceite para que a ligação entre as duas seja
   * visível. Dentro de um `<details>` fechado ela era pulada por todo
   * mundo — que foi como o sistema chegou a dez «sem local registrado»
   * em dez amostras.
   */
  const [locais, setLocais] = useState<LocalOpcao[] | null>(null);
  useEffect(() => {
    api<LocalOpcao[]>('/locais?situacao=ativo')
      .then(setLocais)
      .catch(() => setLocais([]));
  }, []);

  const precisaLocal = d.decisao !== '' && d.decisao !== 'rejeicao';

  /*
   * A origem de cada amostra, e o que continua por chegar (onda 251).
   *
   * A conferência é a última chance de ver que uma prevista ficou de fora
   * — ou que a amostra que chegou não foi ligada à prevista dela e vai
   * entrar sem ensaio. As colunas só aparecem quando a OS tem previsão:
   * sem ela, «sem previsão» em toda linha seria ruído.
   */
  const comPrevisao = previstas.length > 0;
  const prevista = (id: string | null) => (id ? previstas.find((p) => p.id === id) ?? null : null);
  const ligadas = new Set(d.amostras.map((a) => a.osAmostraPrevistaId).filter(Boolean));
  const ficam = porChegar(previstas).filter((p) => !ligadas.has(p.id));

  return (
    <>
      {/* 268: o escopo da OS, na mesma faixa da OS e da Programação. */}
      {d.osId && <EscopoDaOs osId={d.osId} />}
      {semMinimo > 0 && (
        <div className="aviso erro" role="status">
          <strong>
            {plural(semMinimo, 'amostra está', 'amostras estão')} sem as duas fotos obrigatórias.
          </strong>
          {' '}O recebimento vai ser gravado, mas fica como <b>rascunho</b> até as
          fotos entrarem — e sem conclusão não sai o relatório de recebimento.
        </div>
      )}

      {/*
        * A âncora do campo, e a marca de que ele é o que falta.
        *
        * `data-campo` é o endereço para onde a recusa leva; `.em-falta`
        * é a cara que ele ganha até ser respondido. Sem a segunda, o
        * link levava o olho até três botões que continuavam com a mesma
        * aparência de sempre — e o técnico chegava ao lugar certo sem
        * saber que tinha chegado.
        */}
      <div className={`cartao${emFalta === 'decisao' ? ' em-falta' : ''}`}
        data-campo="decisao">
        <h2>Decisão do recebimento</h2>
        <Escolha
          rotulo="O laboratório aceita este material?"
          erro={emFalta === 'decisao'
            ? 'Escolha uma das três — é ela que define a situação de cada '
              + 'amostra, e o recebimento não se grava sem ela.'
            : undefined}
          classe="decisao"
          opcoes={DECISOES}
          valor={d.decisao}
          limpavel={false}
          aoEscolher={(v) => mudar('decisao', v ?? '')}
          dica="A decisão não muda depois de registrada — é ela que define a situação de cada amostra."
        />
        <Area
          rotulo="Divergências em relação ao solicitado"
          value={d.divergencias}
          maxLength={2000}
          rows={3}
          placeholder="ex.: previstas 9 amostras, recebidas 7"
          onChange={(e) => mudar('divergencias', e.target.value)}
        />
        <Area
          rotulo="Ressalvas"
          value={d.ressalvas}
          maxLength={2000}
          rows={3}
          placeholder="ex.: tubo Shelby SP-04 sem tampa em uma das extremidades; material exposto"
          onChange={(e) => mudar('ressalvas', e.target.value)}
        />

        {precisaLocal && (
          <div data-campo="local"
            className={emFalta === 'local' ? 'em-falta' : undefined}>
            <Escolha
              rotulo="Onde este material foi posto?"
              erro={emFalta === 'local'
                ? 'Escolha o local — o recebimento não se grava sem ele.'
                : undefined}
              opcoes={(locais ?? []).map((l) => ({
                valor: l.id,
                texto: `${l.nome} · ${l.tipo_nome}`,
              }))}
              valor={d.herdado.localId}
              limpavel={false}
              aoEscolher={(v) => aoMudarHerdado({ localId: v ?? '' })}
              dica={
                'Vale para o lote inteiro. É a única hora em que alguém '
                + 'está com o material na mão — e a bancada de recebimento '
                + 'é uma resposta legítima: o lugar tem que ser verdadeiro, '
                + 'não definitivo.'
              }
            />
            {locais !== null && locais.length === 0 && (
              <div className="aviso erro" role="status">
                <strong>Nenhum local de armazenamento cadastrado.</strong>{' '}
                O recebimento precisa dizer onde o material ficou (§6), e o
                cadastro está vazio.
                {/*
                  O link, e não só a instrução.

                  Este aviso aparece exatamente no dia da implantação, com
                  alguém segurando uma caixa. Mandar essa pessoa procurar
                  um menu é o atrito que faz o recebimento ser feito no
                  papel «só desta vez» — e o «só desta vez» é como um
                  sistema perde o primeiro mês de dados.

                  Abre em outra aba de propósito: o rascunho do
                  recebimento está na memória desta, e sair dela para
                  cadastrar o perderia.
                */}
                {' '}
                <a href="/locais" target="_blank" rel="noreferrer">
                  Cadastrar agora
                </a>{' '}
                — a prateleira do depósito, a câmara úmida e uma «bancada
                de recebimento» são um começo suficiente. Depois volte
                aqui e escolha.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="cartao">
        <h2>Conferência</h2>
        <table className="dados">
          <tbody>
            <tr><th style={{ width: '38%' }}>Cliente</th><td>{d.clienteNome ?? <span className="nada">—</span>}</td></tr>
            <tr><th>OS</th><td>{d.osNumero ?? <span className="nada">sem OS — nenhum ensaio começa até atribuir</span>}</td></tr>
            <tr><th>Origem</th><td>{rotulo(d.origem) || <span className="nada">—</span>}</td></tr>
            <tr><th>Entregue por</th><td>
              {d.origem === 'equipe_propria'
                ? (d.entregaEquipeId ? <EquipeNome id={d.entregaEquipeId} /> : <span className="nada">—</span>)
                : ([d.entregaNome, d.entregaEmpresa].filter((x) => x.trim()).join(' — ')
                   || <span className="nada">—</span>)}
            </td></tr>
            <tr><th>Amostras</th><td>{plural(d.amostras.length, 'amostra', 'amostras')}</td></tr>
            <tr>
              <th>Fotos</th>
              <td>
                {fotos ? plural(fotos, 'foto', 'fotos') : <span className="nada">nenhuma</span>}
                {d.amostras.length > 0 && (
                  <span className="sub">
                    {' '}· {d.amostras.length - semMinimo} de {d.amostras.length} com o mínimo
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/*
          * 269 (etapa D): a conferência é uma LISTA TOCÁVEL, agrupada por
          * furo. Era uma tabela de seis colunas que rolava de lado no
          * celular, com o furo e a profundidade fora da vista, e a linha não
          * levava a lugar nenhum (achado 7). Agora cada amostra é um botão
          * que volta à tela dela; o furo é o cabeçalho do grupo; a amostra
          * sem as fotos obrigatórias diz «faltam fotos».
          */}
        <div className="conferencia-por-furo">
          {gruposPorFuro(d.amostras).map(([furo, itens]) => (
            <section key={furo || '—'} className="grupo-furo">
              <h3 className="furo-cab">
                {furo || 'Sem furo'}{' '}
                <span className="n mono">{plural(itens.length, 'amostra', 'amostras')}</span>
              </h3>
              <ul className="lista-conferencia">
                {itens.map(({ a, i }) => {
                  const p = prevista(a.osAmostraPrevistaId);
                  const falta = !minimoCumprido(listaDeFotos, i);
                  const prof = faixaProfundidade(
                    paraNumero(a.profundidadeInicial), paraNumero(a.profundidadeFinal));
                  return (
                    <li key={a.chave}>
                      <button type="button" className={`item-conferencia${falta ? ' falta' : ''}`}
                        title={`Abrir a amostra ${i + 1}`}
                        onClick={() => aoAbrirAmostra(i)}>
                        <span className="num-amostra mono">{i + 1}</span>
                        <span className="corpo-item">
                          <span className="ident">
                            {a.identificacaoRecebida || <span className="nada">sem identificação</span>}
                          </span>
                          <span className="sub">
                            {[prof && `${prof} m`, rotulo(a.tipoAmostra),
                              a.tipoAcondicionamento === 'outro'
                                ? a.acondicionamentoDescricao || rotulo('outro')
                                : rotulo(a.tipoAcondicionamento)]
                              .filter(Boolean).join(' · ') || '—'}
                          </span>
                          {comPrevisao && (
                            <span className="sub">
                              {p
                                ? <span className="selo ok">Prevista na OS</span>
                                : <span className="selo atencao">Sem previsão</span>}
                              {' '}
                              {p
                                ? (ensaiosDaPrevista(p) || 'nenhum ensaio marcado')
                                : 'sem ensaio — programe na OS'}
                            </span>
                          )}
                        </span>
                        <span className="estado-item">
                          {falta
                            ? <span className="selo atencao">Faltam fotos</span>
                            : <span className="selo ok">Pronta</span>}
                          <span className="seta" aria-hidden="true">›</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        {ficam.length > 0 && (
          <p className="sub continuam-por-chegar">
            <b>Continuam por chegar:</b>{' '}
            {ficam.map(nomeDaPrevista).join(', ')}. Elas não entram neste
            lote, e a OS continua esperando por elas.
          </p>
        )}
      </div>
    </>
  );
}


/* ── A equipe que entregou (0164) ─────────────────────────────────── */

/**
 * As equipes ativas da casa, como botões — igual ao «recebido por».
 *
 * Uma lista de dois ou três nomes não merece uma busca; e o técnico que
 * está com a caixa na mão sabe de que equipe ela veio.
 */
function EquipeQueEntregou({ valor, erro, aoEscolher }: {
  valor: string | null; erro?: string; aoEscolher: (id: string | null) => void;
}) {
  const [equipes, setEquipes] = useState<Equipe[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    verEquipes().then((l) => setEquipes(l.filter((e) => e.situacao === 'ativa')))
      .catch(() => setFalhou(true));
  }, []);
  if (falhou) {
    return (
      <div className="aviso atencao">
        Seu perfil não consulta o cadastro de equipes. Peça a um gestor.
      </div>
    );
  }
  if (equipes === null) return <Carregando texto="Carregando equipes" />;
  if (equipes.length === 0) {
    return (
      <div className="aviso atencao">
        Nenhuma equipe ativa cadastrada. Cadastre em <b>Campo → Equipes</b>{' '}
        antes de registrar o recebimento.
      </div>
    );
  }
  return (
    <Escolha
      rotulo="Qual equipe trouxe o material?"
      opcoes={equipes.map((e) => ({ valor: e.id, texto: `${e.codigo} · ${e.nome}` }))}
      valor={valor ?? ''}
      erro={erro}
      aoEscolher={(v) => aoEscolher(v || null)}
    />
  );
}

function EquipeNome({ id }: { id: string }) {
  const [nome, setNome] = useState<string | null>(null);
  useEffect(() => {
    verEquipes().then((l) => {
      const e = l.find((x) => x.id === id);
      setNome(e ? `Equipe ${e.codigo} — ${e.nome}` : null);
    }).catch(() => setNome(null));
  }, [id]);
  return <>{nome ?? '…'}</>;
}
